// PARKED (EVA-163 comment 163139) — zone colouring is off; every spacing label
// is black. Kept so restoring it is an uncomment.
// import { OCTANT_ZONE_COLORS } from "constants/octantZone.constants";
import { XDA_LINE_LABEL_A, XDA_LINE_LABEL_A_PRIME } from "constants/xdaBin.constants";
import _sortBy from "lodash/sortBy";
import _zip from "lodash/zip";
import RBush from "rbush";
import Two from "two.js";
import ZUI from "two.js/extras/jsm/zui.js";
import { calculateTextDimensions } from "utils/dom";

import {
  Ipdb3dModel,
  Lsd,
  ScreenSize,
  XdaData,
  XdaLayoutOptions,
  XdaSceneRect
} from "models/xdaData";

import { XDASettingsT } from "components/vis/context/types";
import convertHexToRGBA from "components/vis/util/convertHexToRgba";
import { DUC_DOT_COLOR, DUC_DOT_RADIUS } from "components/vis/util/ducWells";
import getDistanceBetweenPoints from "components/vis/util/getDistanceBetweenPoints";
import {
  OctantThresholds,
  OctantZone,
  SpacingMaxRadii,
  classifyOctantZone,
  isWithinSpacingMaxRadii
} from "components/vis/util/octantZone";

import { IScreenshotSettings } from "../../models/screenshot";
import { EPSILON } from "../../utils/numbers";

const TEXT_PADDING = 10;
// offset for displaying measurement so it doesn't cover the dot
const Y_OFFSET = 15;

// Degenerate viewports can invert the allowed range (min > max); centering is
// the only sensible answer there, and it keeps the result finite.
const clampToRange = (value: number, min: number, max: number) =>
  min > max ? (min + max) / 2 : Math.min(Math.max(value, min), max);

// Text in the scene sizes itself off the plotted width, so a bigger widget draws
// bigger labels instead of the same labels in more space. Floor and slope match the
// spacing chips (SPACING_FONT_MIN, SPACING_FONT_PER_PLOT_WIDTH below); only the
// ceiling differs, because these labels are far denser than one hovered chip and
// stop growing at 16px to keep the plot readable.
export const XDA_LABEL_FONT_MIN = 10;
export const XDA_LABEL_FONT_MAX = 16;
const XDA_LABEL_FONT_PER_PLOT_WIDTH = 0.011;

// The sizes each group drew at before it scaled. They are also the sizes screenshot
// mode keeps, so a saved preset renders exactly as it did.
export const DEFAULT_DATA_LABEL_SIZE = 9;
export const DEFAULT_ZONE_LABEL_SIZE = 9;
export const DEFAULT_TICK_LABEL_SIZE = 10;
// The A / A' end markers draw a step above the "TVD SS" / "Relative" column headers
// beside them, and keep that ratio as both scale, so the ends run to 1.3x whatever the
// headers reach rather than stopping at the ceiling.
export const XDA_END_LABEL_BASE_SIZE = 13;
const XDA_HEADER_BASE_SIZE = DEFAULT_TICK_LABEL_SIZE;
// Where the depth column header sits, measured from the right end of the plot, and how
// far left of it the A' end sat at the old sizes. The header offset is fixed: it centres
// the header over a tick column whose own inset from the axis is fixed. The clearance is
// what scales, so the two labels cannot grow into each other.
const HEADER_X_OFFSET = 65;
const END_LABEL_CLEARANCE = 32;
// A widget far wider than it is tall gains no vertical room, but the label boxes grow
// in both directions, so the plotted height bounds the size as well. Without this a
// wide, short dashboard row either walks a value label away from the dot it belongs to
// or exhausts its collision tries and drops the label with no signal.
const XDA_LABEL_PLOT_ASPECT_LIMIT = 2.5;

// A screenshot preset size arrives through IScreenshotSettings' index signature, so the
// compiler only knows it as string | number | boolean, and a field the user left blank
// is persisted as 0. Coerce once and fall back to the size the group drew at before it
// scaled, which is what every saved preset was tuned against.
const presetSizeOr = (value: unknown, baseSize: number): number => {
  if (typeof value !== "number" && typeof value !== "string") {
    return baseSize;
  }
  const size = Number(value);
  return Number.isFinite(size) && size > 0 ? size : baseSize;
};

export const getXdaLabelFontSize = (plotWidth: number): number =>
  clampToRange(
    XDA_LABEL_FONT_PER_PLOT_WIDTH * plotWidth,
    XDA_LABEL_FONT_MIN,
    XDA_LABEL_FONT_MAX
  );

// The live canvas's own margins and axis gap. The margins that hold the labels grow from
// these (getLiveLayoutGrid), but the size the labels draw at is always measured against
// these base values, never the grown ones: the right margin depends on which depth
// columns are shown, so measuring against it would make switching a column on resize
// every label in the scene, the X-axis numbers included. Same reason the size does not
// come from computeActualDimensions, whose Y-axis buffer moves with those toggles too.
export const LIVE_GRID_BASE = { top: 50, left: 90, right: 115, bottom: 50 };
export const LIVE_AXIS_GAP = 20;

// The size the labels scale to, bounded by the aspect limit above.
export const getLiveLabelSize = (screenSize: Maybe<ScreenSize>): number => {
  const plotWidth = (screenSize?.width ?? 0) - LIVE_GRID_BASE.left - LIVE_GRID_BASE.right;
  const plotHeight =
    (screenSize?.height ?? 0) - LIVE_GRID_BASE.top - LIVE_GRID_BASE.bottom;
  return getXdaLabelFontSize(
    Math.min(plotWidth, plotHeight * XDA_LABEL_PLOT_ASPECT_LIMIT)
  );
};
// EVA-24: XDA "Show Data" value labels sit directly on top of busy map/chart
// content, so each one carries its own outline to stay legible. This block is the
// single place that rationale lives — the other halo touchpoints point back here.
//
// The outline is a stroke on the label's own Two.Text, not a blur filter or a
// stack of offset copies:
//   • One node per label. `paint-order: stroke` paints the stroke UNDER the glyph
//     on the same <text>, so there are no halo copies to keep in draw order, to
//     duplicate in the accessibility tree and text selection, or to multiply by
//     the four collision passes drawValueLabels runs over every well.
//   • It scales for export (AC 5). `linewidth` is in the same user units as
//     `size`, so deriving one from the other keeps the outline at the same
//     relative weight at the default 9 and at whatever pt a screenshot preset
//     asks for.
//   • It survives rasterisation (AC 2). `stroke`, `linewidth` and `className` are
//     entries in Two.Text.Properties that the SVG renderer writes out as the plain
//     `stroke` / `stroke-width` / `class` presentation attributes — the same way
//     `fill` and `size` already reach the DOM. Export goes through snapdom
//     (components/screenshot/utils/captureElementToClipboard.ts), which clones the
//     DOM and inlines computed style onto every cloned element, `paint-order`
//     among them. Nothing here depends on a `url(#id)` reference resolving inside
//     that clone, and there is no document-global id for a second mounted
//     XdaViewer to collide with.
//
// SVG's default `miter` linejoin spikes at acute glyph corners (the top of a "4"),
// and the default `paint-order: fill stroke` would paint the outline OVER the
// glyph and eat half its weight. Neither is reachable through the Two.js API, so
// both come from a stylesheet injected once per Two.js instance — see
// ensureDataLabelHaloStyle.
export const DATA_LABEL_HALO_CLASS = "xda-data-label-halo";
// Hardcoded to match the sibling labels in this file, which stay legible the same
// way: the spacing-measurement chip's text background (`textBackground.fill =
// "white"`) and the octant-zone label background (`background.fill = "#ffffff"`,
// "stay legible over any background via the solid white fill"). No dark theme
// ships today — nothing in src/ or public/ ever adds the `.dark` class, and the
// screenshot container is hardcoded white in both themes (XdaViewer.tsx's
// XDAContainer) — so when one does ship, all three of these move together.
export const DATA_LABEL_HALO_COLOR = "white";
// A stroke is centred on the glyph outline, so a third of the font size reaches
// size/6 outward. That stays inside the smallest nudge the collision passes below
// apply, which is two ninths of the font size (nudgeStep), so a neighbouring label's
// outline can't reach into an already-placed glyph. Both sides are fractions of the
// same font size, so the relationship holds at every size, screenshot presets included:
// the nudges follow the preset size the same way the halo does.
export const DATA_LABEL_HALO_WIDTH_RATIO = 1 / 3;

// How many times a collision pass may nudge a value label before it gives up and drops
// the label. Each attempt moves it one nudge step further from its dot, so the budget is
// also a leash: at 20 a label can end up about nine of its own heights away from the
// well it describes. The steps are all fractions of the label size, so that distance
// holds at every size, and 20 is where a crowded section draws in full without a label
// wandering far enough to read as another well's. It was 10, which dropped a fifth of
// the labels on a tight stack.
const LABEL_PLACEMENT_ATTEMPTS = 20;

const SVG_NS = "http://www.w3.org/2000/svg";
const HALO_STYLE_MARKER = `data-${DATA_LABEL_HALO_CLASS}`;

// Adds the `paint-order` / `stroke-linejoin` rule this instance's labels need (see
// DATA_LABEL_HALO_CLASS for why those two can't be Two.js properties). Called once
// per draw rather than once per label, and idempotent: context.clear() empties the
// scene group, not <defs>, so the rule outlives every redraw.
//
// The rule is byte-identical for every instance and references no ids, so two
// concurrently mounted XdaViewers each end up with their own self-contained copy —
// unmounting one cannot strand the other.
function ensureDataLabelHaloStyle(context: { renderer?: { defs?: Element } }): void {
  const defs = context?.renderer?.defs;
  if (!defs || defs.querySelector(`style[${HALO_STYLE_MARKER}]`)) {
    return;
  }
  const style = document.createElementNS(SVG_NS, "style");
  style.setAttribute(HALO_STYLE_MARKER, "");
  style.textContent = `.${DATA_LABEL_HALO_CLASS} { paint-order: stroke; stroke-linejoin: round; stroke-linecap: round; }`;
  defs.appendChild(style);
}

// Every "Show Data" value label is drawn with this shape: the well's colour on top
// of the shared halo stroke (see DATA_LABEL_HALO_CLASS).
export interface DataLabelStyle {
  size: number;
  fill: string;
  stroke: string;
  linewidth: number;
  className: string;
}

export const dataLabelStyle = (size: number, fill: string): DataLabelStyle => ({
  size,
  fill,
  stroke: DATA_LABEL_HALO_COLOR,
  linewidth: size * DATA_LABEL_HALO_WIDTH_RATIO,
  className: DATA_LABEL_HALO_CLASS
});

// Extra horizontal margin fitPointToView/unfitPointFromView reserve for Y-axis depth
// labels. Single source of truth for both transforms (and their tests) so a future
// change to this rule can't silently desync the forward and inverse math.
export const getYAxisBuffer = (xdaSettings: XDASettingsT): number => {
  if (xdaSettings.showRelativeDepth && xdaSettings.showTVDSSDepth) {
    return 0;
  }
  if (xdaSettings.showRelativeDepth || xdaSettings.showTVDSSDepth) {
    return 40;
  }
  return 80;
};

// Shared by fitPointToView and unfitPointFromView - the actual plotted-area pixel
// span, after reserving grid chrome and the Y-axis label buffer.
export const computeActualDimensions = (
  options: XdaLayoutOptions,
  screenSize: ScreenSize,
  xdaSettings: XDASettingsT
): { actualWidth: number; actualHeight: number } => ({
  actualWidth:
    screenSize.width -
    options.grid.left -
    options.grid.right +
    getYAxisBuffer(xdaSettings),
  actualHeight: screenSize.height - options.grid.top - options.grid.bottom
});

const fitPointToView = (
  x: number,
  y: number,
  sceneWidth: number,
  sceneHeight: number,
  options: XdaLayoutOptions,
  screenSize: ScreenSize,
  xdaSettings: XDASettingsT
) => {
  const { actualWidth, actualHeight } = computeActualDimensions(
    options,
    screenSize,
    xdaSettings
  );
  const px = options.grid.left + (x / sceneWidth) * actualWidth;
  const py = options.grid.top + (y / sceneHeight) * actualHeight;
  return [px, py];
};

// Inverse of fitPointToView: given a screen/surface pixel position, recover the
// scene-space (x, y) that fitPointToView would have placed there. Used by the
// measurement tool so a traced distance always agrees with whatever gridlines/data
// are currently on screen, instead of maintaining a separate, easily-drifting
// approximation of this same transform.
const unfitPointFromView = (
  px: number,
  py: number,
  sceneWidth: number,
  sceneHeight: number,
  options: XdaLayoutOptions,
  screenSize: ScreenSize,
  xdaSettings: XDASettingsT
): [number, number] => {
  const { actualWidth, actualHeight } = computeActualDimensions(
    options,
    screenSize,
    xdaSettings
  );
  // Guard against a collapsed plotted area (FR-005): never divide by a zero/negative
  // pixel span, clamp to a minimum of 1 before dividing.
  const x = ((px - options.grid.left) / Math.max(actualWidth, 1)) * sceneWidth;
  const y = ((py - options.grid.top) / Math.max(actualHeight, 1)) * sceneHeight;
  return [x, y];
};

// The depth tick numbers are drawn into the margin right of the plot: the first column
// starts TICK_COLUMN_INSET past the axis, and with both depth columns on the second
// sits a gap further right again. The gap has its own font-size floor, below which the
// two columns would run together.
const TICK_COLUMN_INSET = 25;
const TICK_COLUMN_GAP_PAD = 9;
// The header row's own pad, 4px tighter than the columns' since it always has been.
const HEADER_ROW_GAP_PAD = 5;
const DEPTH_COLUMN_GAP_MIN_FONT = 14;
// The widest label the depth columns draw: TVD SS depths are negative in the scene, so
// the value carries a sign as well as its digits.
const WIDEST_TICK_VALUE = "-00000";

//gets the gap needed to space Relative and TVD SS labels
// liveFontSize is the size the tick labels draw at outside screenshot mode. The gap
// keeps its old floor, so a small widget spaces the two depth columns exactly as it did;
// past that floor the gap widens with the numbers it separates.
const getGapFontSize = (
  settings?: IScreenshotSettings,
  liveFontSize = DEPTH_COLUMN_GAP_MIN_FONT
) => {
  const defaultFontSize = Math.max(DEPTH_COLUMN_GAP_MIN_FONT, liveFontSize);
  if (
    settings?.gridLabelsFontSize != null &&
    Number(settings?.gridLabelsFontSize) > defaultFontSize
  ) {
    return calculateTextDimensions(
      "00000",
      Number(settings.gridLabelsFontSize),
      "sans-serif"
    ).width;
  }
  return calculateTextDimensions("00000", defaultFontSize, "sans-serif").width;
};

// The two depth columns are drawn a pitch apart, and their headers sit above them at the
// same pitch. Live, that pitch is the digit gap the columns have always used, widened
// when the two header words need more room than it: that way the headers stay the size of
// the numbers they label instead of being shrunk, and the pair can never touch.
const RELATIVE_HEADER = "Relative";
const TVDSS_HEADER = "TVD SS";

// Both terms are measured the same way, through calculateTextDimensions, so the pitch is
// one consistent quantity rather than a mix of a real measurement and an estimate.
const headerWidthAt = (text: string, fontSize: number): number =>
  calculateTextDimensions(text, fontSize, "sans-serif").width;

// The columns and the headers above them have always used slightly different pads, so
// each keeps its own; what they share is the floor the two header words impose.
const getLiveDepthColumnPitch = (fontSize: number, pad: number): number =>
  Math.max(
    getGapFontSize(undefined, fontSize) + pad,
    (headerWidthAt(RELATIVE_HEADER, fontSize) + headerWidthAt(TVDSS_HEADER, fontSize)) /
      2 +
      LABEL_MARGIN_SAFETY
  );

// The right margin is a fixed part of the layout, so past the middle of the ramp the
// tick row is wider than the space reserved for it and the SVG viewport clips the
// digits. Cap the size by the space actually left of the canvas edge: the plot's right
// edge sits at grid.right less the Y-axis buffer, which fitPointToView adds to the
// plotted width, so that buffer is margin the ticks do not get.
const getLiveTickLabelSize = (
  options: XdaLayoutOptions,
  screenSize: Maybe<ScreenSize>,
  xdaSettings: XDASettingsT
): number => {
  const fontSize = getLiveLabelSize(screenSize);
  // With neither depth column on, nothing is drawn in the right margin, and the Y-axis
  // buffer has taken almost all of it anyway. Capping there would pin the size to its
  // floor for the default view, on behalf of columns that do not exist.
  if (!xdaSettings.showRelativeDepth && !xdaSettings.showTVDSSDepth) {
    return fontSize;
  }
  const bothColumns = xdaSettings.showRelativeDepth && xdaSettings.showTVDSSDepth;
  // Only one column's text scales against the margin. The gap between two columns is
  // measured with the same call the draw makes and holds its own floor, so it comes out
  // of the space available rather than scaling with the size.
  const marginWidth = Math.max(
    options.grid.right -
      getYAxisBuffer(xdaSettings) -
      TICK_COLUMN_INSET -
      (bothColumns ? getLiveDepthColumnPitch(fontSize, TICK_COLUMN_GAP_PAD) : 0),
    1
  );
  return fitLabelToMargin(
    fontSize,
    textWidthAt(WIDEST_TICK_VALUE, fontSize),
    marginWidth,
    DEFAULT_TICK_LABEL_SIZE
  );
};

// See presetSizeOr for the screenshot rule.
const getTickLabelSize = (
  options: XdaLayoutOptions,
  screenSize: Maybe<ScreenSize>,
  xdaSettings: XDASettingsT,
  screenshotSettings: IScreenshotSettings,
  screenshotOverlayVisible: boolean
): number =>
  screenshotOverlayVisible
    ? presetSizeOr(screenshotSettings?.gridLabelsFontSize, DEFAULT_TICK_LABEL_SIZE)
    : getLiveTickLabelSize(options, screenSize, xdaSettings);

function drawYAxis(
  context,
  rect: XdaSceneRect,
  options: XdaLayoutOptions,
  screenSize: ScreenSize,
  xdaSettings: XDASettingsT,
  screenshotSettings: IScreenshotSettings,
  screenshotOverlayVisible: boolean
) {
  drawYAxisLine(context, rect, options, screenSize, xdaSettings);
  if (xdaSettings.showRelativeDepth || xdaSettings.showTVDSSDepth) {
    drawYAxisTicks(
      context,
      rect,
      options,
      screenSize,
      xdaSettings,
      screenshotSettings,
      screenshotOverlayVisible
    );
  }
}

function drawXAxis(
  context,
  rect: XdaSceneRect,
  options: XdaLayoutOptions,
  screenSize: ScreenSize,
  xdaSettings: XDASettingsT,
  screenshotSettings: IScreenshotSettings,
  screenshotOverlayVisible: boolean
) {
  drawXAxisLine(context, rect, options, screenSize, xdaSettings);
  if (xdaSettings.showGrid) {
    drawXAxisTicks(
      context,
      rect,
      options,
      screenSize,
      xdaSettings,
      screenshotSettings,
      screenshotOverlayVisible
    );
  }
}

export function drawAAPrimeLabel(
  context,
  rect: XdaSceneRect,
  options: XdaLayoutOptions,
  screenSize: ScreenSize,
  xdaSettings: XDASettingsT,
  screenshotSettings: IScreenshotSettings,
  screenshotOverlayVisible: boolean,
  isXdaLineReversed = false
) {
  let [x, y] = fitPointToView(
    0,
    0,
    rect.widthScaled,
    rect.height,
    options,
    screenSize,
    xdaSettings
  );
  x -= options.axisGap;
  y -= options.axisGap;
  // These labels have no screenshot preset of their own, so screenshot mode keeps the
  // two sizes they have always drawn at and only the live viewer scales. The headers
  // follow the tick size, cap included, so each one stays the width of the depth
  // column it heads; the A / A' ends keep their step above the headers.
  const headerSize = screenshotOverlayVisible
    ? XDA_HEADER_BASE_SIZE
    : getLiveTickLabelSize(options, screenSize, xdaSettings);
  const endSize = (XDA_END_LABEL_BASE_SIZE / XDA_HEADER_BASE_SIZE) * headerSize;
  const endClearance = END_LABEL_CLEARANCE * (headerSize / XDA_HEADER_BASE_SIZE);
  const leftLabel = isXdaLineReversed ? XDA_LINE_LABEL_A_PRIME : XDA_LINE_LABEL_A;
  const rightLabel = isXdaLineReversed ? XDA_LINE_LABEL_A : XDA_LINE_LABEL_A_PRIME;
  context.makeText(leftLabel, x + 10, y - 5, { size: endSize });
  const [width] = fitPointToView(
    rect.widthScaled,
    rect.height,
    rect.width,
    rect.height,
    options,
    screenSize,
    xdaSettings
  );

  const headerY = y - 5;
  if (xdaSettings.showTVDSSDepth) {
    let gap = 0;
    if (xdaSettings.showRelativeDepth) {
      gap = screenshotOverlayVisible
        ? getGapFontSize(screenshotSettings) + HEADER_ROW_GAP_PAD
        : getLiveDepthColumnPitch(headerSize, HEADER_ROW_GAP_PAD);
    }
    context.makeText(
      TVDSS_HEADER,
      width - options.axisGap + gap + HEADER_X_OFFSET,
      headerY,
      {
        size: headerSize
      }
    );
  }
  if (xdaSettings.showRelativeDepth) {
    context.makeText(
      RELATIVE_HEADER,
      width - options.axisGap + HEADER_X_OFFSET,
      headerY,
      {
        size: headerSize
      }
    );
  }
  if (!xdaSettings.showRelativeDepth && !xdaSettings.showTVDSSDepth) {
    context.makeText(rightLabel, width + 10, headerY, { size: endSize });
  } else {
    context.makeText(
      rightLabel,
      width - options.axisGap + HEADER_X_OFFSET - endClearance,
      headerY,
      { size: endSize }
    );
  }
}

export function drawBoundingBox(
  context,
  rect: XdaSceneRect,
  options: XdaLayoutOptions,
  screenSize: ScreenSize,
  xdaSettings: XDASettingsT
) {
  let [x, y] = fitPointToView(
    0,
    0,
    rect.widthScaled,
    rect.height,
    options,
    screenSize,
    xdaSettings
  );
  x -= options.axisGap;
  y -= options.axisGap;
  let [width, height] = fitPointToView(
    rect.widthScaled,
    rect.height,
    rect.width,
    rect.height,
    options,
    screenSize,
    xdaSettings
  );
  height -= y - options.axisGap;
  width -= x - options.axisGap;
  const rectangle = context.makeRectangle(x + width / 2, y + height / 2, width, height);
  rectangle.linewidth = 1;
  rectangle.stroke = "lightgray";
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function drawAxis(
  context,
  data: XdaData,
  options: XdaLayoutOptions,
  screenSize: ScreenSize,
  xdaSettings: XDASettingsT,
  screenshotSettings: IScreenshotSettings,
  screenshotOverlayVisible: boolean,
  isXdaLineReversed = false
): void {
  const rect = data?.rect;
  if (!rect) {
    return;
  }

  drawBoundingBox(context, rect, options, screenSize, xdaSettings);
  drawAAPrimeLabel(
    context,
    rect,
    options,
    screenSize,
    xdaSettings,
    screenshotSettings,
    screenshotOverlayVisible,
    isXdaLineReversed
  );
  if (
    xdaSettings.showGrid ||
    xdaSettings.showTVDSSDepth ||
    xdaSettings.showRelativeDepth
  ) {
    drawXAxis(
      context,
      rect,
      options,
      screenSize,
      xdaSettings,
      screenshotSettings,
      screenshotOverlayVisible
    );
    drawYAxis(
      context,
      rect,
      options,
      screenSize,
      xdaSettings,
      screenshotSettings,
      screenshotOverlayVisible
    );
  }
}

// The old hover guide-lines display (dashed L-lines from the hovered well to
// every other well with raw purple diffX / black diffY numbers) was removed in
// favor of the spacing measurement overlay below, which shows the same
// distances with legible, unit-suffixed chips. (EVA-29; the chips were
// zone-colored until EVA-163 comment 163139 turned the colouring off.)

function drawYAxisLine(
  context,
  rect: XdaSceneRect,
  options: XdaLayoutOptions,
  screenSize: ScreenSize,
  xdaSettings: XDASettingsT
) {
  const scaledWidth = options.scale * rect.width;
  let [tx, ty] = fitPointToView(
    0,
    0,
    rect.width,
    rect.height,
    options,
    screenSize,
    xdaSettings
  );
  let [bx, by] = fitPointToView(
    0,
    rect.height,
    scaledWidth,
    rect.height,
    options,
    screenSize,
    xdaSettings
  );
  tx -= options.axisGap;
  ty -= options.axisGap;
  bx -= options.axisGap;
  by += options.axisGap;
  const line = context.makeLine(tx, ty, bx, by);
  line.linewidth = 3;
}

function drawXAxisLine(
  context,
  rect: XdaSceneRect,
  options: XdaLayoutOptions,
  screenSize: ScreenSize,
  xdaSettings: XDASettingsT
) {
  let [tx, ty] = fitPointToView(
    0,
    rect.height,
    rect.widthScaled,
    rect.height,
    options,
    screenSize,
    xdaSettings
  );
  let [bx, by] = fitPointToView(
    rect.widthScaled,
    rect.height,
    rect.width,
    rect.height,
    options,
    screenSize,
    xdaSettings
  );

  tx -= options.axisGap;
  ty += options.axisGap;
  bx += options.axisGap;
  by += options.axisGap;
  const line = context.makeLine(tx, ty, bx, by);
  line.linewidth = 3;
}

export function drawYAxisTicks(
  context,
  rect: XdaSceneRect,
  options: XdaLayoutOptions,
  screenSize: ScreenSize,
  xdaSettings: XDASettingsT,
  screenshotSettings: IScreenshotSettings,
  screenshotOverlayVisible: boolean
) {
  const numberOfTicks = 10.0;
  const tickTextSize = getTickLabelSize(
    options,
    screenSize,
    xdaSettings,
    screenshotSettings,
    screenshotOverlayVisible
  );
  // Constant for the whole draw: hoisted out of the tick loop because
  // getGapFontSize measures text through the DOM, one forced layout per call.
  const columnGap =
    xdaSettings.showRelativeDepth && xdaSettings.showTVDSSDepth
      ? screenshotOverlayVisible
        ? getGapFontSize(screenshotSettings) + TICK_COLUMN_GAP_PAD
        : getLiveDepthColumnPitch(tickTextSize, TICK_COLUMN_GAP_PAD)
      : 0;
  const originalMinY = rect.miny;
  const originalMaxY = rect.maxy;
  // round to nearest to the original min and max y
  const startY = Math.sign(originalMaxY) * Math.floor(Math.abs(originalMaxY) / 10) * 10;
  const endY = Math.sign(originalMinY) * Math.ceil(Math.abs(originalMinY) / 10) * 10;
  const offset = originalMaxY - startY;
  let tickSize = Math.floor((startY - endY) / numberOfTicks);
  tickSize = Math.ceil(Math.abs(tickSize) / 10) * 10;
  const dividerCoords = [];
  for (let i = 0; i <= numberOfTicks; i++) {
    const stepSize = i * tickSize;
    // Since we already calculate the start and offset, should start with startY instead of originalMaxY
    const value = startY - i * tickSize;
    const relativeValue = i * tickSize;
    const [x, y] = fitPointToView(
      rect.widthScaled,
      stepSize,
      rect.width,
      rect.height,
      options,
      screenSize,
      xdaSettings
    );

    // If value is less than the last tick, show the last grid line at y
    if (value <= rect.miny || (y > rect.height && i > 10)) {
      dividerCoords.push(y);
      //show at least 10 grid lines before checking if it's outside
      break;
    }

    if (xdaSettings.showTVDSSDepth) {
      const text = context.makeText(
        Math.round(value + offset),
        x + columnGap + TICK_COLUMN_INSET,
        y
      );
      text.size = tickTextSize;
      text.alignment = "left";
    }

    if (xdaSettings.showRelativeDepth) {
      const text1 = context.makeText(Math.round(relativeValue), x + TICK_COLUMN_INSET, y);
      text1.size = tickTextSize;
      text1.alignment = "left";
    }
    // store where to place the divider line
    if (i === 0) {
      dividerCoords.push(x + columnGap + 20, y - 20);
    } else if (i === numberOfTicks) {
      dividerCoords.push(y);
    }

    // eslint-disable-next-line prefer-const
    let [linex0, liney0] = fitPointToView(
      0,
      stepSize,
      rect.width,
      rect.height,
      options,
      screenSize,
      xdaSettings
    );
    linex0 -= options.axisGap - 1;
    // eslint-disable-next-line prefer-const
    let [linex1, liney1] = fitPointToView(
      rect.widthScaled,
      stepSize,
      rect.width,
      rect.height,
      options,
      screenSize,
      xdaSettings
    );
    linex1 += options.axisGap + 2;
    const axisLine = context.makeLine(linex0, liney0, linex1, liney1);
    axisLine.linewidth = 1;
    axisLine.stroke = "lightgray";
  }

  if (xdaSettings.showRelativeDepth && xdaSettings.showTVDSSDepth) {
    const dividerLine = context.makeLine(
      dividerCoords[0],
      dividerCoords[1] - 15,
      dividerCoords[0],
      dividerCoords[2]
    );
    dividerLine.linewidth = 0.5;
  }
}

export function drawXAxisTicks(
  context,
  rect: XdaSceneRect,
  options: XdaLayoutOptions,
  screenSize: ScreenSize,
  xdaSettings: XDASettingsT,
  screenshotSettings: IScreenshotSettings,
  screenshotOverlayVisible: boolean
) {
  const width = rect.width;
  const height = rect.height;
  const majorTickSteps = Math.floor(rect.width / 10 / 10.0) * 10;
  // These numbers sit under the plot, not in the right margin, so they take the plain
  // live size rather than the depth columns' capped one. Sharing the cap would shrink
  // the distance numbers whenever a depth column was switched on, which has nothing to
  // do with them.
  const tickTextSize = screenshotOverlayVisible
    ? presetSizeOr(screenshotSettings?.gridLabelsFontSize, DEFAULT_TICK_LABEL_SIZE)
    : getLiveLabelSize(screenSize);

  for (let i = 0; i <= 10; i++) {
    const value = i * majorTickSteps * options.scale;
    let [x, y] = fitPointToView(
      value,
      height,
      width,
      height,
      options,
      screenSize,
      xdaSettings
    );
    y += options.axisGap + 20;
    x -= options.axisGap;
    const text = context.makeText(Math.round(value / options.scale), x, y);
    text.size = tickTextSize;

    let [linex0, liney0] = fitPointToView(
      value,
      0,
      width,
      height,
      options,
      screenSize,
      xdaSettings
    );
    liney0 -= options.axisGap;
    linex0 -= options.axisGap;
    let [linex1, liney1] = fitPointToView(
      value,
      height,
      width,
      height,
      options,
      screenSize,
      xdaSettings
    );
    linex1 -= options.axisGap;
    liney1 += options.axisGap + 10;

    const axisLine = context.makeLine(linex0, liney0, linex1, liney1);
    axisLine.linewidth = 1;
    axisLine.stroke = "lightgray";
  }
}

/**
 * EVA-185: which rows the centroid/label pass should paint, or `null` when it
 * should not run at all.
 *
 * A centroid needs its polygon to have drawn: `drewPolygons` is false both when
 * every path was skipped and on a "polygons not in list" render, which shows
 * those outlines alone. Dot-wells carry no polygon, so they are the whole
 * content of a dots-only section and Show Data still has to label them - but
 * they must not drag the suppressed producing wells back onto the canvas with
 * them, so the well rows are dropped instead. All three go together:
 * `well_data` is index-parallel to `ids`/`centroids`, and dropping one without
 * the others would label wells with the wrong rows.
 */
export function centroidPassScene(
  data: XdaData,
  drewPolygons: boolean
): Nullable<XdaData> {
  if (drewPolygons) {
    return data;
  }
  if (!data.duc_wells?.length) {
    return null;
  }
  return { ...data, centroids: [], ids: [], well_data: [] };
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function drawCentroid(
  context,
  data: XdaData,
  options: XdaLayoutOptions,
  screenSize: ScreenSize,
  xdaSettings: XDASettingsT,
  screenshotSettings: IScreenshotSettings,
  screenshotOverlayVisible: boolean,
  showData: boolean, //whether to show the data on the well or not
  // EVA-29: collects the value-label Text objects so the spacing measurement
  // can hide them while its own display is showing (they occupy the same
  // space around the dots and clash with the measurement chips).
  valueLabelsRegistry?: { current: { objects: object[] } }
): void {
  const rect = data.rect;
  const colorDict = {};
  const randomColor = [
    "#a50026",
    "#d73027",
    "#f46d43",
    "#67001f",
    "#01665e",
    "#762a83",
    "#c51b7d",
    "#8c510a",
    "#74add1",
    "#4575b4",
    "#313695",
    "#542788",
    "#00441b"
  ];
  const colorLen = randomColor.length;
  for (let i = 0; i < data.centroids.length; i++) {
    const centroid = data.centroids[i];
    const [x, y] = fitPointToView(
      centroid.x,
      centroid.y,
      rect.width,
      rect.height,
      options,
      screenSize,
      xdaSettings
    );
    const circle = context.makeCircle(x, y, 3);
    circle.fill = showData ? `${randomColor[i % colorLen]}` : "#212121";
    colorDict[data.ids[i].unique_id] = circle.fill;
  }
  // EVA-185: the dot-wells are drawn by drawDucWells, not here, but their
  // labels go through the same drawValueLabels pass below and look their text
  // colour up in this dictionary. They keep the dot's own colour rather than
  // joining the per-index rotation above, so a label still points back at a
  // mark the reader can find.
  for (const duc of data.duc_wells ?? []) {
    colorDict[duc.unique_id] = DUC_DOT_COLOR;
  }
  // Reset here, not inside drawValueLabels: the registry's lifetime must match
  // this context's scene. Turning Well Data off skips the call below, which
  // would otherwise leave a dead generation of Text nodes pinned in the
  // registry — the same retention class as the EVA-6911 invariant.
  if (valueLabelsRegistry?.current) {
    valueLabelsRegistry.current.objects = [];
  }
  if (showData) {
    drawValueLabels(
      context,
      data,
      options,
      screenSize,
      xdaSettings,
      screenshotSettings,
      screenshotOverlayVisible,
      colorDict,
      valueLabelsRegistry
    );
  }
  context.update();
}

// EVA-185: dot-only wells. Plain dots in the same frame as the centroids — no
// polygon, no event binding — so they can neither be hovered into the spacing
// measurement nor take drainage area from a neighbour. Their Show Data labels
// are NOT drawn here: they ride drawValueLabels with the producing wells, so
// one collision tree covers both. The service
// returns `duc_wells` only when the toggle is on, so an empty array is the "off"
// state and this is a no-op; callers do not re-check the setting.
export function drawDucWells(
  context,
  data: XdaData,
  options: XdaLayoutOptions,
  screenSize: ScreenSize,
  xdaSettings: XDASettingsT
): void {
  const ducWells = data?.duc_wells ?? [];
  if (!ducWells.length || !data.rect) {
    return;
  }
  const rect = data.rect;
  for (const duc of ducWells) {
    const [x, y] = fitPointToView(
      duc.point.x,
      duc.point.y,
      rect.width,
      rect.height,
      options,
      screenSize,
      xdaSettings
    );
    // Fill only, exactly as drawCentroid does for a well dot — same size, same
    // colour, same default stroke — so a dot-well is indistinguishable from a
    // well centroid. The absent bubble is the whole visual difference. (EVA-185)
    const dot = context.makeCircle(x, y, DUC_DOT_RADIUS);
    dot.fill = DUC_DOT_COLOR;
    // Scene-graph identity for the dot, matching the convention `drawShape`
    // uses for well paths: Two.js's own `id` is left alone (EVA-6823) and the
    // UWI travels on a custom prop. `isDucDot` distinguishes these from the
    // centroid circles drawn into the same scene. Nothing in production reads
    // either yet - they exist so a hit-test can tell a dot-well from a well
    // without re-deriving it from coordinates, and the draw test uses them to
    // select the dots.
    dot.uwi = duc.unique_id;
    dot.isDucDot = true;
  }
  context.update();
}

// A Two.Text held in the Well Data value-label registry. `baseOpacity` is ours,
// not Two.js's: trackLabel stamps it at construction so setValueLabelsVisibility
// can restore each object to ITS OWN opacity rather than a hardcoded 1, which
// would permanently brighten anything drawn below full opacity the first time the
// spacing-measurement overlay toggles labels back on.
interface TrackedLabel {
  visible?: boolean;
  opacity?: number;
  baseOpacity?: number;
}

// EVA-29: show/hide the collected Well Data value labels. Used to suppress the
// old per-dot value display while the spacing measurement overlay is showing,
// and restore it when the overlay clears.
export function setValueLabelsVisibility(
  valueLabelsRegistry: Maybe<{ current: { objects: object[] } }>,
  context,
  visible: boolean
): void {
  if (!valueLabelsRegistry?.current) {
    return;
  }
  for (const obj of valueLabelsRegistry.current.objects as TrackedLabel[]) {
    obj.visible = visible;
    // Restore to this object's own base opacity, not a hardcoded 1 - see TrackedLabel.
    obj.opacity = visible ? obj.baseOpacity ?? 1 : 0;
  }
  context.update();
}

function drawValueLabels(
  context,
  data: XdaData,
  options: XdaLayoutOptions,
  screenSize: ScreenSize,
  xdaSettings: XDASettingsT,
  screenshotSettings: IScreenshotSettings,
  screenshotOverlayVisible: boolean,
  colorDict: { [id: string]: string },
  valueLabelsRegistry?: { current: { objects: object[] } }
) {
  // Capture each label's own opacity right after construction - see TrackedLabel.
  const trackLabel = (label: TrackedLabel) => {
    label.baseOpacity = label.opacity ?? 1;
    valueLabelsRegistry?.current?.objects.push(label);
    return label;
  };
  // A row the four passes below can read: all three parts present. The passes
  // reach straight into `item[0].x`, `item[1].unique_id` and `item[2][n]`, so a
  // row missing any of them throws and takes the whole render down with it.
  type LabelRow = [XdaData["centroids"][number], XdaData["ids"][number], string[]];
  // `_zip` pads the short lists with `undefined`, so this filter is what used to
  // be the `!well_data` early return: that return could not survive EVA-185,
  // because a section can hold nothing but dot-wells, in which case `well_data`
  // is legitimately empty and the rows live on the dots instead. Filtering per
  // row rather than bailing keeps the guarantee for a partial `well_data` too.
  const zipped = _zip(data.centroids, data.ids, data.well_data);
  const wellRows = zipped.filter(
    (row): row is LabelRow => !!row[0] && !!row[1] && !!row[2]
  );
  // The dots ride the same placement passes as the wells: one collision tree
  // covers both, so a dot label cannot land on a well label. They are shaped to
  // match a zipped row - position, identity, data - and their position is
  // already in the same scene frame as a centroid.
  const ducRows: LabelRow[] = (data.duc_wells ?? [])
    .filter((duc) => duc.well_data?.length)
    .map((duc) => [
      duc.point,
      { unique_id: duc.unique_id, formatted_id: duc.formatted_id },
      duc.well_data
    ]);
  if (wellRows.length === 0 && ducRows.length === 0) {
    return;
  }
  const dataZipped: LabelRow[] = [...wellRows, ...ducRows];

  // Only the live viewer scales, which is what makes a full-screen widget legible.
  const textSize = screenshotOverlayVisible
    ? presetSizeOr(screenshotSettings?.dataLabelsFontSize, DEFAULT_DATA_LABEL_SIZE)
    : getLiveLabelSize(screenSize);
  // Every box metric below was tuned against DEFAULT_DATA_LABEL_SIZE, so all of them
  // scale with the size the labels actually draw at. Left at their old pixel values,
  // larger labels would overlap, and the nudge steps would be too small to resolve a
  // collision inside the 10 tries each pass allows. This holds in screenshot mode too:
  // the boxes follow the preset size, so the spacing a preset lays out matches the
  // glyphs it draws. A preset that leaves the size blank resolves to the default, which
  // keeps its scale at 1 and its labels exactly where they always were.
  const labelScale = textSize / DEFAULT_DATA_LABEL_SIZE;
  const yOffset = 9 * labelScale;
  // Rough advance width per character, also used as the gap between a dot and its
  // label. The narrower estimate below is what the other three passes measure with.
  const widthPerChar = 6 * labelScale;
  const charWidthEstimate = 5 * labelScale;
  // How far each collision pass shifts a label that lands on an already-placed one.
  // The halo stroke is sized to stay inside the smallest of these - see
  // DATA_LABEL_HALO_WIDTH_RATIO.
  const nudgeRight = 3 * labelScale;
  const nudgeStep = 2 * labelScale;
  const nudgeBelow = 4 * labelScale;

  // The boxes grow with the label size but the dots do not, so a section gets denser in
  // box units as the size rises and a fixed budget of nudges stops being enough: at a
  // 60pt preset a large pad lost a tenth of its values, with nothing on screen saying so.
  // The budget grows with the label instead, which keeps the reach in step with the boxes
  // it has to clear. It never falls below the base, so a preset smaller than the default
  // still gets every attempt it used to.
  const attempts = Math.max(
    LABEL_PLACEMENT_ATTEMPTS,
    Math.ceil(LABEL_PLACEMENT_ATTEMPTS * labelScale)
  );
  const rect = data.rect;
  const tree = new RBush();
  // Once per draw, not once per label - see DATA_LABEL_HALO_CLASS.
  ensureDataLabelHaloStyle(context);
  // One Two.Text per label, outline and all - see DATA_LABEL_HALO_CLASS. Shared by
  // all four collision passes below so the centring math has a single home.
  const drawLabel = (
    value: string,
    box: { minX: number; minY: number; maxX: number; maxY: number },
    textStyle: DataLabelStyle
  ) =>
    trackLabel(
      context.makeText(
        value,
        box.minX + (box.maxX - box.minX) / 2,
        box.minY + (box.maxY - box.minY) / 2,
        textStyle
      )
    );
  const sortedByLeftToRight = _sortBy(dataZipped, [(item) => item[0].x]);
  for (const item of sortedByLeftToRight) {
    const [x, y] = fitPointToView(
      item[0].x,
      item[0].y,
      rect.width,
      rect.height,
      options,
      screenSize,
      xdaSettings
    );
    const textStyle = dataLabelStyle(textSize, colorDict[item[1].unique_id]);
    const value = item[2][3];
    if (!value || value === "n/a") {
      continue;
    }
    const valueWidth = (value.length * widthPerChar) / 2.0;
    let box = {
      minX: x + widthPerChar,
      minY: y - yOffset / 2,
      maxX: x + widthPerChar + valueWidth * 2,
      maxY: y + yOffset / 2,
      value
    };
    let i = 0;
    let xOffSet = 0;
    while (tree.search(box).length > 0) {
      i += 1;
      xOffSet = i * nudgeRight;
      box = {
        minX: x + widthPerChar + xOffSet, //increment to the right each time
        minY: y - yOffset / 2,
        maxX: x + widthPerChar + valueWidth * 2 + xOffSet,
        maxY: y + yOffset / 2,
        value
      };
      if (i > attempts) {
        break;
      }
    }
    if (i > attempts) {
      // no clear room found inside the attempt budget
      continue;
    }
    tree.insert(box);
    drawLabel(value, box, textStyle);
  }

  const sortedByRightToLeft = sortedByLeftToRight.reverse();
  for (const item of sortedByRightToLeft) {
    const [x, y] = fitPointToView(
      item[0].x,
      item[0].y,
      rect.width,
      rect.height,
      options,
      screenSize,
      xdaSettings
    );
    const textStyle = dataLabelStyle(textSize, colorDict[item[1].unique_id]);
    const value = item[2][1];
    if (!value || value === "n/a") {
      continue;
    }
    const halfWidthOfText = (value.length * charWidthEstimate) / 2.0;
    let box = {
      minX: x - widthPerChar - halfWidthOfText * 2,
      minY: y - yOffset / 2,
      maxX: x - widthPerChar,
      maxY: y + yOffset / 2,
      value
    };
    let i = 0;
    let collisionOffset = 0;
    const spacing = 0;
    let collisionOffsetY = 0;
    while (tree.search(box).length > 0) {
      i += 1;
      collisionOffset = i * nudgeStep;
      collisionOffsetY = i * nudgeStep;
      box = {
        minX: x - spacing - halfWidthOfText * 2 - collisionOffset, //increment to the right each time
        minY: y - yOffset / 2 + collisionOffsetY,
        maxX: x - spacing - collisionOffset,
        maxY: y + yOffset / 2 + collisionOffset,
        value
      };
      if (i > attempts) {
        break;
      }
    }
    if (i > attempts) {
      // no clear room found inside the attempt budget
      continue;
    }
    tree.insert(box);
    drawLabel(value, box, textStyle);
  }

  const sortedByTopToBottom = _sortBy(dataZipped, [(item) => item[0].y]);
  for (const item of sortedByTopToBottom) {
    const [x, y] = fitPointToView(
      item[0].x,
      item[0].y,
      rect.width,
      rect.height,
      options,
      screenSize,
      xdaSettings
    );
    const value = item[2][0];
    if (!value || value === "n/a") {
      continue;
    }
    const textStyle = dataLabelStyle(textSize, colorDict[item[1].unique_id]);
    const halfWidthOfText = (value.length * charWidthEstimate) / 2.0;
    const ySpacing = 4 * labelScale; //y spacing so that it doesn't touch the dot
    let box = {
      minX: x - halfWidthOfText,
      minY: y + ySpacing,
      maxX: x + halfWidthOfText,
      maxY: y + ySpacing + yOffset,
      value
    };
    let i = 0;
    let collisionOffset = 0;
    let collisionOffsetY = 0;
    while (tree.search(box).length > 0) {
      i += 1;
      collisionOffset = i * nudgeBelow;
      collisionOffsetY = i * nudgeBelow;
      box = {
        minX: x - halfWidthOfText, //increment to the right each time
        minY: y + ySpacing + collisionOffsetY,
        maxX: x + halfWidthOfText,
        maxY: y + ySpacing + yOffset + collisionOffset,
        value
      };
      if (i > attempts) {
        break;
      }
    }
    if (i > attempts) {
      // no clear room found inside the attempt budget
      continue;
    }
    tree.insert(box);
    drawLabel(value, box, textStyle);
  }
  const sortedByBottomToTop = sortedByTopToBottom.reverse();
  for (const item of sortedByBottomToTop) {
    const [x, y] = fitPointToView(
      item[0].x,
      item[0].y,
      rect.width,
      rect.height,
      options,
      screenSize,
      xdaSettings
    );
    const value = item[2][2];
    if (!value || value === "n/a") {
      continue;
    }
    const textStyle = dataLabelStyle(textSize, colorDict[item[1].unique_id]);
    const halfWidthOfText = (value.length * charWidthEstimate) / 2.0;
    const ySpacing = 4 * labelScale; //y spacing so that it doesn't touch the dot
    let box = {
      minX: x - halfWidthOfText,
      minY: y - ySpacing - yOffset,
      maxX: x + halfWidthOfText,
      maxY: y - ySpacing,
      value
    };
    let i = 0;
    let collisionOffset = 0;
    let collisionOffsetY = 0;
    while (tree.search(box).length > 0) {
      i += 1;
      collisionOffset = i * nudgeStep;
      collisionOffsetY = i * nudgeStep;
      box = {
        minX: x - halfWidthOfText, //increment to the right each time
        minY: y - ySpacing - yOffset - collisionOffset,
        maxX: x + halfWidthOfText,
        maxY: y - ySpacing - collisionOffsetY,
        value
      };
      if (i > attempts) {
        break;
      }
    }
    if (i > attempts) {
      // no clear room found inside the attempt budget
      continue;
    }
    tree.insert(box);
    drawLabel(value, box, textStyle);
  }
}

// Plain hover hit-test cell returned by drawIpdb3dModel — see EVA-6911
// (spec archived on the Jira ticket). Field layout is
// frozen to what XdaViewer.tsx's manual hit-test (isPointInsideRectangle /
// getRectangleUnderMouse) already reads from the old Two.js rectangles.
export interface IpdbCell {
  translation: { x: number; y: number };
  width: number;
  height: number;
  // Ipdb3dModel.val is `val?: number` — callers pass `undefined` through
  // (never `null`), hence Maybe rather than Nullable.
  value: Maybe<number>;
  fill: string;
}

// ZUI zoom is limited to 4x (`zui.addLimits(0.1, 4)`, XdaViewer.tsx). A 2x raster
// keeps the ipdb grid bitmap acceptably crisp at max zoom (flat-filled cells, no
// fine linework) without the extra memory a 4x raster would cost — subject to
// the MAX_RASTER_DIM/MAX_RASTER_AREA ceiling below.
const IPDB_RASTER_SCALE = 2;
// ponytail: browsers cap canvas dimensions (~16384px per side on Chrome/Firefox),
// but the binding constraint in this browserslist (which includes Safari) is
// iOS/iPadOS Safari's much tighter ~16.7 megapixel (4096x4096-equivalent area)
// canvas cap. A wide XDA line combined with a high Scale Width setting
// (widthScaled 1-10 multiplies box x, XdaViewer.tsx ~734-742) can push a flat 2x
// raster past that limit, silently producing a blank/unusable canvas (toDataURL
// returns a blank image — no thrown error, no visible failure). MAX_RASTER_AREA
// is set at the iOS Safari ceiling (dominant over MAX_RASTER_DIM in practice) so
// the raster degrades gracefully (lower effective resolution) instead of going
// blank; MAX_RASTER_DIM still guards the per-side Chrome/Firefox cap.
const MAX_RASTER_DIM = 8192;
const MAX_RASTER_AREA = 4096 * 4096;

interface IpdbGridCacheEntry {
  canvas: HTMLCanvasElement;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  texture: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sprite: any;
}

// Reused per Two.js context (one entry each for the live canvas and the
// screenshot canvas) instead of creating a new canvas + Two.Texture + Two.Sprite
// on every renderScene call.
//
// Two.Texture registers every canvas source in its session-global,
// never-cleared Texture.ImageRegistry (node_modules/two.js/build/two.js
// ~6621-6628, keyed by '#' + texture.id) — a fresh Two.Texture per call leaks
// one full-grid canvas (tens of MB) into that registry per render, since
// context.clear() only detaches the Two.js scene node, it never touches the
// registry. Reusing the same Texture/Sprite/canvas keeps the registry key
// stable across renders, so re-registering (by reassigning texture.image
// below) overwrites the same entry instead of growing it.
//
// IMPORTANT: this WeakMap does NOT get cleaned up "automatically" on unmount —
// two.js unconditionally pushes every `new Two(...)` instance into its
// session-global `Two.Instances` array (node_modules/two.js/build/two.js
// ~16943) and never removes it, so the Two context itself (this WeakMap's key)
// is never garbage collected for the lifetime of the tab, no matter how many
// times the owning component mounts/unmounts. Callers MUST call
// releaseIpdbGridResources(context) from their own unmount/teardown path (see
// XdaViewer.tsx's setup-effect and screenshot-instance-effect cleanups) or both
// this cache entry and its Texture.ImageRegistry entry leak permanently.
const ipdbGridCache = new WeakMap<object, IpdbGridCacheEntry>();

// Call from a Two.js context's teardown path (e.g. a useEffect cleanup) once
// it will never be drawn into again. Removes both this module's cache entry
// AND the corresponding Two.Texture.ImageRegistry entry — see the ipdbGridCache
// comment above for why the latter can't be left to "just" get garbage
// collected.
export function releaseIpdbGridResources(context: object): void {
  const entry = ipdbGridCache.get(context);
  if (!entry) {
    return;
  }
  // Texture.Register.canvas (node_modules/two.js/build/two.js ~6621-6628) only
  // sets texture._src (and therefore the texture.src getter used as the
  // registry key) once a real Two.js render pass has actually processed this
  // texture at least once. If that never happened (e.g. unmounted before the
  // first paint, or a headless/never-rendered test context), texture.src is
  // still undefined and Registry#remove is a harmless no-op on that key.
  const registryKey = entry.texture.src;
  if (registryKey) {
    Two.Texture.ImageRegistry.remove(registryKey);
  }
  ipdbGridCache.delete(context);
}

export function drawIpdb3dModel(
  context,
  data: XdaData,
  options: XdaLayoutOptions,
  screenSize: ScreenSize,
  xdaSettings: XDASettingsT,
  getColor: (ipdb: Ipdb3dModel) => { value: Maybe<number>; color: string },
  // Optional: when provided, a hover highlight rect is (re)created here, in the
  // same relative draw-order position as the ipdb sprite (before zone
  // lines/wells/tops are drawn later in renderScene), so it always paints
  // *below* them. XdaViewer.tsx's mousemove/mouseout handlers then only
  // reposition/show/hide this existing rect — they never call context.add()
  // themselves, which would otherwise append it at the end of the scene graph
  // (i.e. on top of everything). See EVA-6911 and src/components/vis/CONTEXT.md.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  highlightRectRef?: { current: any }
): Maybe<IpdbCell[]> {
  if (!data.ipdb3d_boxes) {
    return;
  }
  const rect = data.rect;
  const cells: IpdbCell[] = [];
  let extentMinX = Infinity;
  let extentMaxX = -Infinity;
  let extentMinY = Infinity;
  let extentMaxY = -Infinity;

  for (const cell of data.ipdb3d_boxes.sort((a, b) => {
    if (Math.abs(a.left.x - b.left.x) < EPSILON) {
      return a.top - b.top;
    }
    return a.left.x - b.left.x;
  })) {
    const [minX, maxY] = fitPointToView(
      Math.max(0, cell.left.x),
      cell.top,
      rect.width,
      rect.height,
      options,
      screenSize,
      xdaSettings
    );
    const [maxX, minY] = fitPointToView(
      cell.right.x,
      cell.bot,
      rect.width,
      rect.height,
      options,
      screenSize,
      xdaSettings
    );
    const width = maxX - minX;
    const height = maxY - minY;
    const cx = minX + width / 2;
    const cy = Math.min(maxY, minY) - height / 2.0;

    const { value, color } = getColor(cell);
    const fill = xdaSettings.includeZero || value != 0 ? color : "white";

    cells.push({
      translation: { x: cx, y: cy },
      width,
      height,
      value,
      fill
    });

    // Track the grid's overall screen extent (both possible corners per axis,
    // since width/height can be negative depending on line direction — same
    // quirk the old per-cell Two.js rectangles were already tolerant of).
    const left = cx - width / 2;
    const right = cx + width / 2;
    const top = cy - height / 2;
    const bottom = cy + height / 2;
    extentMinX = Math.min(extentMinX, left, right);
    extentMaxX = Math.max(extentMaxX, left, right);
    extentMinY = Math.min(extentMinY, top, bottom);
    extentMaxY = Math.max(extentMaxY, top, bottom);
  }

  if (cells.length > 0) {
    const extentWidth = extentMaxX - extentMinX;
    const extentHeight = extentMaxY - extentMinY;

    // Clamp the raster scale so the canvas never exceeds MAX_RASTER_DIM per
    // side or MAX_RASTER_AREA in total area — see the ponytail comment above
    // IPDB_RASTER_SCALE. Falls back to the ideal scale on a degenerate
    // (zero-width or zero-height) extent, where the corresponding term would
    // otherwise divide by zero.
    const rasterScale = Math.min(
      IPDB_RASTER_SCALE,
      extentWidth > 0 ? MAX_RASTER_DIM / extentWidth : IPDB_RASTER_SCALE,
      extentHeight > 0 ? MAX_RASTER_DIM / extentHeight : IPDB_RASTER_SCALE,
      extentWidth > 0 && extentHeight > 0
        ? Math.sqrt(MAX_RASTER_AREA / (extentWidth * extentHeight))
        : IPDB_RASTER_SCALE
    );

    // Clamp each dimension to an integer canvas size. Ceiling extentWidth and
    // extentHeight independently (and only THEN clamping each to
    // MAX_RASTER_DIM) can overshoot MAX_RASTER_AREA even when rasterScale was
    // computed to respect it exactly — e.g. a 5000x4000 extent at the
    // area-bound rasterScale ceils to ~4581x3665, whose product is a few
    // thousand pixels over MAX_RASTER_AREA. canvasHeight is therefore also
    // floor-divided by the ALREADY-clamped canvasWidth, guaranteeing
    // canvasWidth * canvasHeight <= MAX_RASTER_AREA exactly.
    const canvasWidth = Math.max(
      1,
      Math.min(Math.ceil(extentWidth * rasterScale), MAX_RASTER_DIM)
    );
    const canvasHeight = Math.max(
      1,
      Math.min(
        Math.ceil(extentHeight * rasterScale),
        MAX_RASTER_DIM,
        Math.floor(MAX_RASTER_AREA / canvasWidth)
      )
    );

    // The area-floor clamp above can shave canvasHeight (rarely canvasWidth)
    // below what a single uniform `rasterScale` would imply. Using PER-AXIS
    // raster scale (rather than re-deriving one shared, more conservative
    // scale) keeps the sprite's rendered scene-space size exactly equal to
    // the true grid extent on both axes — no cropped sliver — while still
    // using the full resolution each axis's clamped integer dimension allows.
    // Two.Shape#scale natively supports a Two.Vector for independent x/y
    // scaling (node_modules/two.js/build/two.js ~2793-2814 property setter,
    // ~15275-15290 SVG renderer matrix/scale application) — verified against
    // the pinned two.js@0.7.13 in node_modules, not assumed.
    const rasterScaleX = extentWidth > 0 ? canvasWidth / extentWidth : rasterScale;
    const rasterScaleY = extentHeight > 0 ? canvasHeight / extentHeight : rasterScale;

    // Rasterize every cell into ONE offscreen canvas instead of one Two.js
    // rectangle per cell — collapses 10k-50k SVG <rect> nodes into a single
    // scene child (see research.md "Decision: Rasterize the ipdb grid...").
    // The canvas/texture/sprite triple is reused per context (see
    // ipdbGridCache above) rather than recreated every call.
    let entry = ipdbGridCache.get(context);
    if (!entry) {
      const canvas = document.createElement("canvas");
      const texture = new Two.Texture(canvas);
      const sprite = new Two.Sprite(texture, 0, 0);
      entry = { canvas, texture, sprite };
      ipdbGridCache.set(context, entry);
    }
    const { canvas, texture, sprite } = entry;

    // Resizing the canvas implicitly clears its previous pixel content, so no
    // separate clearRect is needed even when the extent is unchanged.
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
    const ctx = canvas.getContext("2d");

    // jsdom guard: getContext("2d") returns null in jsdom (no canvas
    // polyfill). Skip texture/sprite paint + scene attachment but keep
    // returning the full cell array so hover hit-test data stays testable
    // independently of rasterization.
    if (ctx) {
      for (const cell of cells) {
        ctx.fillStyle = cell.fill;
        const rawLeft = (cell.translation.x - cell.width / 2 - extentMinX) * rasterScaleX;
        const rawRight =
          (cell.translation.x + cell.width / 2 - extentMinX) * rasterScaleX;
        const rawTop = (cell.translation.y - cell.height / 2 - extentMinY) * rasterScaleY;
        const rawBottom =
          (cell.translation.y + cell.height / 2 - extentMinY) * rasterScaleY;
        // Snap each cell's edges outward to integer pixels so adjacent cells
        // slightly overdraw instead of leaving antialiasing hairline seams
        // between them.
        const pixelLeft = Math.floor(Math.min(rawLeft, rawRight));
        const pixelRight = Math.ceil(Math.max(rawLeft, rawRight));
        const pixelTop = Math.floor(Math.min(rawTop, rawBottom));
        const pixelBottom = Math.ceil(Math.max(rawTop, rawBottom));
        ctx.fillRect(pixelLeft, pixelTop, pixelRight - pixelLeft, pixelBottom - pixelTop);
      }

      sprite.translation.set(extentMinX + extentWidth / 2, extentMinY + extentHeight / 2);
      // Sprite._update() (node_modules/two.js/build/two.js ~9153-9188) only
      // overwrites width/height from the texture image's pixel dimensions
      // once `texture.loaded` is true — which itself only flips true partway
      // through the SAME render tick's SVG serialization pass, AFTER the
      // scene-graph's own per-shape _update() (where Sprite._update() reads
      // that flag) has already run. On the very first paint of a brand-new
      // sprite this ordering would leave it sized 0x0 for a tick (and
      // permanently 0x0 in these jsdom tests, which never invoke a real
      // Two.js render pass at all) — so these assignments are NOT redundant;
      // keep them.
      sprite.width = canvasWidth;
      sprite.height = canvasHeight;
      if (sprite.scale instanceof Two.Vector) {
        sprite.scale.set(1 / rasterScaleX, 1 / rasterScaleY);
      } else {
        sprite.scale = new Two.Vector(1 / rasterScaleX, 1 / rasterScaleY);
      }
      // Same canvas element, new pixel content — reassigning `.image` re-flags
      // the texture so the next render re-serializes it via toDataURL()
      // instead of reusing a stale image. Because this is the SAME
      // Two.Texture instance, texture.id (and therefore its
      // Texture.ImageRegistry key, '#' + texture.id) never changes, so this
      // overwrites the existing registry entry rather than adding a new one.
      texture.image = canvas;
      if (!sprite.parent) {
        context.add(sprite);
      }
    }
  }

  // The highlight rect is recreated in the same relative draw-order position
  // every render (right after the ipdb sprite above, before zone
  // lines/wells/tops are drawn later in the same renderScene pass) so its
  // z-order stays correct. context.clear() at the top of every renderScene
  // pass already drops the previous one, so recreating unconditionally here
  // mirrors that lifecycle instead of trying to reuse a possibly-detached rect.
  if (highlightRectRef) {
    const highlightRect = context.makeRectangle(0, 0, 1, 1);
    highlightRect.linewidth = 0;
    highlightRect.visible = false;
    highlightRectRef.current = highlightRect;
  }

  return cells;
}

// Horizontal gap between a zone name and the axis it labels. Shared by the cap below
// and every place a name is drawn, so the space measured is the space used.
const ZONE_LABEL_INSET = 5;

// Fitting exactly to the margin leaves the last glyph's stem on the edge, and the
// measurement itself rounds up, so keep a few pixels back from it.
const LABEL_MARGIN_SAFETY = 3;

// Shrinks a scaled size until the text fits the space it is drawn into, and never below
// the size that text used to draw at. Both label groups that sit in a fixed grid margin
// need this: past the middle of the ramp their text is wider than the margin reserves,
// and the SVG viewport clips whatever runs past the edge.
function fitLabelToMargin(
  fontSize: number,
  textWidthAtFontSize: number,
  marginWidth: number,
  baseSize: number
): number {
  const usableWidth = Math.max(marginWidth - LABEL_MARGIN_SAFETY, 1);
  if (textWidthAtFontSize <= usableWidth) {
    return fontSize;
  }
  return Math.max(baseSize, (fontSize * usableWidth) / textWidthAtFontSize);
}

// Zone names are right-aligned into the margin left of the plot, so their scaled size
// is capped by what that margin holds. Measures every labelled name once per draw and
// takes the widest, and only from the entries the draw loop labels (the first of each
// list), so an unlabelled long name cannot shrink the names on screen.
// Text width is linear in font size for one family, so each name is measured through
// the DOM once, at a reference size, and scaled from there. Without this a section with
// forty tops forces forty synchronous layouts on every draw, and a draw runs on data,
// resize and several settings changes.
const ZONE_NAME_REFERENCE_SIZE = 10;
const zoneNameWidths = new Map<string, number>();

// Only the first entry of each list is labelled, so these are the names on screen.
const labelledZoneNames = (data: XdaData): string[] =>
  [...(data?.lsds?.[0]?.zones ?? []), ...(data?.otherLsds?.[0]?.zones ?? [])].map(
    (zone) => String(zone?.zone ?? "")
  );

const zoneNameWidthAt = (name: string, fontSize: number): number => {
  if (!name) {
    return 0;
  }
  let referenceWidth = zoneNameWidths.get(name);
  if (referenceWidth === undefined) {
    referenceWidth = textWidthAt(name, ZONE_NAME_REFERENCE_SIZE);
    zoneNameWidths.set(name, referenceWidth);
  }
  return (referenceWidth * fontSize) / ZONE_NAME_REFERENCE_SIZE;
};

export const fitZoneLabelToMargin = (
  fontSize: number,
  data: XdaData,
  options: XdaLayoutOptions
): number => {
  const marginWidth = Math.max(options.grid.left - options.axisGap - ZONE_LABEL_INSET, 1);
  // Widest, not longest: a short name in wide glyphs still has to fit the margin.
  const widestName = labelledZoneNames(data).reduce(
    (widest, name) => Math.max(widest, zoneNameWidthAt(name, fontSize)),
    0
  );
  return fitLabelToMargin(fontSize, widestName, marginWidth, DEFAULT_ZONE_LABEL_SIZE);
};

// A margin reserved for labels can never take so much of the canvas that the plot
// collapses. computeActualDimensions subtracts both margins with no floor and
// fitPointToView applies the result unclamped, so a non-positive plotted width draws the
// section right to left, while unfitPointFromView clamps to 1 — the forward and inverse
// transforms then disagree and the ruler and the spacing overlay report distances
// unrelated to what is on screen. Past this share of the canvas the labels go back to
// being shrunk to fit instead (fitLabelToMargin).
const MAX_MARGIN_SHARE_PER_SIDE = 0.35;

/**
 * The margin to reserve for a group of labels: what they need, never less than the
 * layout's own base, and never more than a share of the canvas. With no canvas width to
 * measure against, the base is the only safe answer.
 */
export const boundLabelMargin = (
  needed: number,
  baseMargin: number,
  canvasWidth: number
): number => {
  if (!(canvasWidth > 0)) {
    return baseMargin;
  }
  return Math.min(Math.max(baseMargin, needed), canvasWidth * MAX_MARGIN_SHARE_PER_SIDE);
};

/**
 * Width the left margin needs to hold this scene's zone names at a given size,
 * including the gap they keep from the axis. The live layout reserves this so the names
 * can grow; fitZoneLabelToMargin is what holds them in if a layout does not.
 */
export const getZoneNameMarginNeeded = (
  data: XdaData,
  fontSize: number,
  axisGap: number
): number => {
  const widest = labelledZoneNames(data).reduce(
    (width, name) => Math.max(width, zoneNameWidthAt(name, fontSize)),
    0
  );
  return widest > 0 ? widest + axisGap + ZONE_LABEL_INSET + LABEL_MARGIN_SAFETY : 0;
};

/**
 * Width the right margin needs to hold the depth columns at a given size: the inset
 * from the axis, one signed depth value, the gap when there are two columns, and the
 * Y-axis buffer, which fitPointToView adds to the plotted width and so takes out of
 * this margin before the columns get any of it.
 */
export const getTickRowMarginNeeded = (
  fontSize: number,
  xdaSettings: XDASettingsT
): number => {
  if (!xdaSettings.showRelativeDepth && !xdaSettings.showTVDSSDepth) {
    return 0;
  }
  const bothColumns = xdaSettings.showRelativeDepth && xdaSettings.showTVDSSDepth;
  return (
    getYAxisBuffer(xdaSettings) +
    TICK_COLUMN_INSET +
    textWidthAt(WIDEST_TICK_VALUE, fontSize) +
    (bothColumns ? getLiveDepthColumnPitch(fontSize, TICK_COLUMN_GAP_PAD) : 0) +
    LABEL_MARGIN_SAFETY
  );
};

/**
 * The grid the live canvas draws with: the base margins, widened to hold whatever this
 * scene's labels need at the size they will draw at.
 *
 * Nothing is reserved while the labels are still at the ramp floor. A small widget is
 * pinned there, so it would be paying plot width for no legibility it did not already
 * have — and the width it would pay is decided by how long a formation name happens to
 * be. Above the floor the reservation is bounded per side (boundLabelMargin), and
 * fitLabelToMargin shrinks whatever still does not fit.
 */
export const getLiveLayoutGrid = (
  screenSize: Maybe<ScreenSize>,
  data: XdaData,
  xdaSettings: XDASettingsT
): XdaLayoutOptions["grid"] => {
  const labelSize = getLiveLabelSize(screenSize);
  if (labelSize <= XDA_LABEL_FONT_MIN) {
    return { ...LIVE_GRID_BASE };
  }
  const canvasWidth = screenSize?.width ?? 0;
  return {
    ...LIVE_GRID_BASE,
    left: boundLabelMargin(
      getZoneNameMarginNeeded(data, labelSize, LIVE_AXIS_GAP),
      LIVE_GRID_BASE.left,
      canvasWidth
    ),
    right: boundLabelMargin(
      getTickRowMarginNeeded(labelSize, xdaSettings),
      LIVE_GRID_BASE.right,
      canvasWidth
    )
  };
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function drawZoneLine(
  context,
  data: XdaData,
  options: XdaLayoutOptions,
  screenSize: Maybe<ScreenSize>,
  xdaSettings: XDASettingsT,
  screenshotSettings: IScreenshotSettings,
  screenshotOverlayVisible: boolean
): void {
  // An empty scene reaches this on several render paths. Bail out before anything reads
  // the rect or the screen size, both of which the sizing below needs.
  const rect = data.rect;
  if (!rect || !screenSize) {
    return;
  }
  // Only the live viewer scales; see presetSizeOr for the screenshot rule.
  const textSize = screenshotOverlayVisible
    ? presetSizeOr(screenshotSettings?.topsLabelsFontSize, DEFAULT_ZONE_LABEL_SIZE)
    : fitZoneLabelToMargin(getLiveLabelSize(screenSize), data, options);
  // The stacking steps below were tuned against DEFAULT_ZONE_LABEL_SIZE. Outside
  // screenshot mode they scale with the live size, or a larger name would sit on top
  // of the one above it; in screenshot mode they stay at their old pixel values so an
  // existing preset stacks its names exactly as it did.
  const labelScale = screenshotOverlayVisible ? 1 : textSize / DEFAULT_ZONE_LABEL_SIZE;
  const crowdedGap = 7 * labelScale;
  const baselineLift = 5 * labelScale;
  const stackStep = 10 * labelScale;
  function extracted(lsds: Lsd[], color: string) {
    let i = 0;
    for (const lsd of lsds) {
      let maxy = -10000;
      let miny = 100000;
      let minx = 100000;
      let maxx = -10000;

      // track bottom of previous text
      let previoustextbot = 0;

      for (const zone of lsd.zones) {
        const line = zone.line;
        const [sx, sy] = fitPointToView(
          i === 0 ? 0 : line.start.x,
          line.start.y,
          rect.width,
          rect.height,
          options,
          screenSize,
          xdaSettings
        );
        const [ex, ey] = fitPointToView(
          line.end.x,
          line.end.y,
          rect.width,
          rect.height,
          options,
          screenSize,
          xdaSettings
        );
        maxy = Math.max(sy, maxy);
        let zoneLine = null;
        if (i === 0) {
          zoneLine = context.makeLine(sx - options.axisGap, sy, ex, ey);

          // first label
          if (previoustextbot === 0) {
            const text = context.makeText(
              zone.zone,
              sx - options.axisGap - ZONE_LABEL_INSET,
              sy - baselineLift
            );
            text.size = textSize;
            text.fill = color;
            text.alignment = "right";
            previoustextbot = sy;
            // if label is too close to the previous one
          } else if (sy - crowdedGap < previoustextbot) {
            const text = context.makeText(
              zone.zone,
              sx - options.axisGap - ZONE_LABEL_INSET,
              previoustextbot + baselineLift
            );
            text.fill = color;
            text.size = textSize;
            text.alignment = "right";
            previoustextbot = previoustextbot + stackStep;
            // if label is not too close
          } else {
            const text = context.makeText(
              zone.zone,
              sx - options.axisGap - ZONE_LABEL_INSET,
              sy - baselineLift
            );
            text.size = textSize;
            text.alignment = "right";
            text.fill = color;
            previoustextbot = sy;
          }
        } else {
          zoneLine = context.makeLine(sx, sy, ex, ey);
        }
        zoneLine.width = 2;
        zoneLine.stroke = color;
        minx = Math.min(sx, minx);
        maxx = Math.max(ex, maxx);
        maxy = Math.max(maxy, sy);
        miny = Math.min(miny, sy);
      }
      if (maxx > 0) {
        context.makeLine(minx, miny, minx, maxy);
        context.makeLine(maxx, miny, maxx, maxy);
      }
      i++;
    }
  }

  if (data.otherLsds?.length > 0) {
    extracted(data.otherLsds, "lightgray");
  }
  extracted(data.lsds, "black");
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const isHighlighted = (
  uniqueId: string,
  highlightWells: readonly string[]
): boolean => highlightWells.includes(uniqueId);

export const drawShape = (
  context,
  data: XdaData,
  options: XdaLayoutOptions,
  screenSize: ScreenSize,
  xdaSettings: XDASettingsT,
  selectedWells,
  onShapeEntered,
  onShapeLeave,
  highlightWells: readonly string[],
  pathsRef,
  showMeasurement,
  showOverlap,
  skipPolygons,
  lineIntersectWells: readonly string[] = [],
  onShapeClicked?: (entryIndex: number) => void
): void => {
  if (!selectedWells) {
    return;
  }
  let i = 0;
  const rect = data.rect;
  pathsRef.current = [];
  const paths = [];
  const pattern = new Two({
    type: Two.Types.canvas,
    width: 32,
    height: 32
  });
  //draw polygons not in well list
  //draw before so other polygon can be above the ones not in the well list
  const line = pattern.makeLine(pattern.width, 0, 0, pattern.height);
  line.stroke = "grey";
  pattern.update();
  for (const poly of data.polygons_not_in_list) {
    const anchors = [];
    for (const point of poly.points) {
      const a = fitPointToView(
        point.x,
        point.y,
        rect.width,
        rect.height,
        options,
        screenSize,
        xdaSettings
      );
      anchors.push(new Two.Anchor(a[0], a[1]));
    }
    const path = context.makePath(anchors, false);
    path.linewidth = 1;
    const texture = context.makeTexture(pattern.renderer.domElement);
    texture.scale = 1;
    texture.repeat = "repeat";
    texture.stroke = "green";
    path.fill = texture;
  }

  const wellLicenseKeys = {};
  const wellStatuses = {};
  const wellGroup = {};
  const colorByLicenseKeys = {};
  const colorByGroupNames = {};
  const prodWells = {};
  data.ids.forEach((id, index) => {
    const dataCount = data.well_data[index]?.length || 0;
    const licenseKey = data.well_data[index]?.[dataCount - 3] || "";
    const status = data.well_data[index]?.[dataCount - 2] || "";
    const groupName = data.well_data[index]?.[dataCount - 1] || "";

    wellLicenseKeys[id.unique_id] = licenseKey;
    wellStatuses[id.unique_id] = status;
    wellGroup[id.unique_id] = groupName;
    if (id.unique_id in selectedWells) {
      const well = selectedWells[id.unique_id];
      // Empty strings must not be used as keys — every well with no license/group
      // would match, causing them to be drawn as dotted laterals instead of crosshatch.
      if (licenseKey) {
        colorByLicenseKeys[licenseKey] = [id.unique_id, well.color];
      }
      if (groupName) {
        colorByGroupNames[groupName] = [id.unique_id, well.color];
      }
    }
  });

  if (!skipPolygons) {
    for (const poly of data.polygons) {
      // Peek at the id before building anchors or creating a Two.js path.
      // makePath() adds the object to the Two.js scene immediately; if we
      // create it and then skip via continue the path still renders as a
      // ghost stroke polygon. Early-exit avoids the scene mutation entirely.
      const id = data.ids[i];
      // Cross-section entry index into data.ids/centroids — stamped on the
      // path because the event-binding loop below iterates only the paths
      // that survived the skip guards, so its own counter can drift from the
      // data index. Spacing selection (EVA-29) anchors this entry index.
      const entryIndex = i;
      const licenseKey = wellLicenseKeys[id.unique_id];
      const inSelectedWells = id.unique_id in selectedWells;
      // eslint-disable-next-line no-prototype-builtins
      const inColorByLicense = colorByLicenseKeys.hasOwnProperty(licenseKey);
      const isInjector = wellStatuses[id.unique_id] === "06. INJ";
      // Well spatially crossed the XDA line (captured by the map intersection
      // query at draw time). If not, it reached the payload only via the
      // selectedWells union and should not appear at all.
      const crossedLine = lineIntersectWells.includes(id.unique_id);

      if (!inSelectedWells && !inColorByLicense && !isInjector && !crossedLine) {
        // Skip before makePath — creating a Two.js path adds it to the scene
        // immediately and would render as a ghost stroke polygon.
        i++;
        continue;
      }

      i++;
      const anchors = [];
      for (const point of poly) {
        const a = fitPointToView(
          point.x,
          point.y,
          rect.width,
          rect.height,
          options,
          screenSize,
          xdaSettings
        );
        anchors.push(new Two.Anchor(a[0], a[1]));
      }
      const path = context.makePath(anchors, false);

      path.linewidth = 3;
      path.stroke = "#212121";
      path.fill = "rgba(0,0,0,0)";
      // Store the UWI in a custom prop, NOT path.id. makePath() already inserted
      // the path into the Two.js scene under its auto-generated id, which the
      // scene's children.ids map is keyed on. Overwriting path.id here (and the
      // license-key reuse below, which produces duplicate ids) desyncs that map,
      // so Two.js clear()/remove() can no longer find these paths to remove them
      // — they accumulate across redraws and stay stuck on the canvas (EVA-6823).
      path.uwi = id.unique_id;
      path.entryIndex = entryIndex;
      if (inSelectedWells) {
        const well = selectedWells[id.unique_id];
        if (isHighlighted(id.unique_id, highlightWells)) {
          path.fill = well.color;
        } else if (showOverlap) {
          path.fill = convertHexToRGBA(well.color, 0.3);
        } else {
          path.fill = "rgba(0,0,0,0)";
        }
        path.stroke = well.color;
      } else if (inColorByLicense) {
        path.uwi = colorByLicenseKeys[licenseKey][0];
        path.stroke = colorByLicenseKeys[licenseKey][1];
        path.licenseKey = licenseKey;
        path.linewidth = 2;
        // A shared licence keeps the sibling's colour but must not hide an injector.
        if (isInjector) path.dashes = [3, 2];
      } else if (isInjector) {
        path.linewidth = 2;
        path.dashes = [3, 2];
        if (
          Object.keys(wellGroup).includes(id.unique_id) &&
          wellGroup[id.unique_id].toLowerCase() !== "subscription required" &&
          colorByGroupNames[wellGroup[id.unique_id]]
        ) {
          path.stroke = colorByGroupNames[wellGroup[id.unique_id]][1] || "#212121"; // Orange for injection wells not in selectedWells
        }
      } else {
        // Well crossed the XDA line but is not in the current filter/selection
        // → show as crosshatch so the user can see out-of-filter intersections.
        const texture = context.makeTexture(pattern.renderer.domElement);
        texture.scale = 1;
        texture.repeat = "repeat";
        path.fill = texture;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (path as any).defaultFill = texture;
        path.linewidth = 1;
      }

      paths.push(path);
      pathsRef.current.push(path);
    }
  }

  context.update();
  for (const path of paths) {
    // Index off the entry stamped on the path, NOT a counter over `paths`:
    // `paths` holds only the polygons that survived the skip guards above, so
    // a counter drifts by one per skipped well and hands the tooltip a
    // different well's row than the one under the pointer — which the spacing
    // overlay (anchored on entryIndex) would then contradict.
    const entryIndex = path.entryIndex ?? 0;
    const wellData = data.well_data[entryIndex];
    let id = data.ids[entryIndex];
    if (path.licenseKey) {
      id = prodWells[path.licenseKey];
    }

    const handleMouseEnter = (evt) => {
      // off when measurement tool is on
      if (!showMeasurement) {
        onShapeEntered(evt, path, id, selectedWells, wellData);
        evt.preventDefault();
      }
    };

    const handleMouseLeave = (evt) => {
      // off when measurement tool is on
      if (!showMeasurement) {
        onShapeLeave(evt, path, id);
        evt.preventDefault();
      }
    };

    // Spacing selection (EVA-29). Ruler wins: while the Measurement tool is
    // enabled its canvas-level click listener owns clicks, so selection is
    // suppressed rather than both firing on the same click.
    const handleClick = (evt) => {
      if (!showMeasurement && onShapeClicked && path.entryIndex != null) {
        onShapeClicked(path.entryIndex);
        evt.preventDefault();
      }
    };

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore
    path._renderer.elem.addEventListener("mouseenter", handleMouseEnter);
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore
    path._renderer.elem.addEventListener("mouseleave", handleMouseLeave);
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore
    path._renderer.elem.addEventListener("click", handleClick);

    // Store cleanup function on the path so callers can remove listeners when paths are redrawn
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore
    path._cleanupListeners = () => {
      path._renderer.elem.removeEventListener("mouseenter", handleMouseEnter);
      path._renderer.elem.removeEventListener("mouseleave", handleMouseLeave);
      path._renderer.elem.removeEventListener("click", handleClick);
    };
  }
};

export const handleMeasurementTool = (
  measurementDataRef,
  showMeasurement: boolean,
  context,
  data: XdaData,
  screenSize: ScreenSize,
  xdaLayoutOptions: XdaLayoutOptions,
  zuiRef: ZUI,
  xdaSettings: XDASettingsT,
  convertLength?: (value: number, precision?: number) => number,
  displayUnit?: string
): void => {
  const eventListener = (evt) => {
    // clear for next line
    if (measurementDataRef.current.dotCounter === 2) {
      clearMeasurementData(measurementDataRef, context, false);
    }
    if (xdaLayoutOptions.scale <= 0) {
      return;
    }
    const clientX = evt.clientX;
    const clientY = evt.clientY;
    const { x, y } = zuiRef.clientToSurface(clientX, clientY);

    const dot = context.makeCircle(x, y, 3);
    dot.fill = "black";
    measurementDataRef.current.dots.push(dot);
    measurementDataRef.current.dotCounter++;

    // Draw line between two dots
    if (measurementDataRef.current.dotCounter === 2) {
      const startDotX = measurementDataRef.current.dots[0].translation.x;
      const startDotY = measurementDataRef.current.dots[0].translation.y;
      const endDotX = measurementDataRef.current.dots[1].translation.x;
      const endDotY = measurementDataRef.current.dots[1].translation.y;
      measurementDataRef.current.line = context.makeLine(
        startDotX,
        startDotY,
        endDotX,
        endDotY
      );
      measurementDataRef.current.line.stroke = "black";
      measurementDataRef.current.line.linewidth = 3;
      context.add(measurementDataRef.current.line);

      if (screenSize) {
        // Recover data-space coordinates by inverting fitPointToView - the same transform
        // used to place every gridline/label/data point on screen - so the ruler always
        // agrees with whatever is currently rendered, regardless of axis-visibility toggles
        // (FR-001/FR-002/FR-003) and without drifting from the real rendering math.
        const [rawX1, rawY1] = unfitPointFromView(
          startDotX,
          startDotY,
          data.rect.width,
          data.rect.height,
          xdaLayoutOptions,
          screenSize,
          xdaSettings
        );
        const [rawX2, rawY2] = unfitPointFromView(
          endDotX,
          endDotY,
          data.rect.width,
          data.rect.height,
          xdaLayoutOptions,
          screenSize,
          xdaSettings
        );
        // xdaLayoutOptions.scale is the same factor scaleData (XdaViewer.tsx) used to
        // pre-multiply centroid.x - not to be confused with rect.widthScaled, a distinct
        // field on XdaData.rect that scaleData also sets (rect.widthScaled = rect.width *
        // scale). It stretches only the x-axis data coordinates, so it must be undone on
        // the x component only - dividing here (rather than inside getDistanceBetweenPoints)
        // keeps that function a plain, symmetric Pythagorean distance (FR-008).
        const x1 = rawX1 / xdaLayoutOptions.scale;
        const y1 = rawY1;
        const x2 = rawX2 / xdaLayoutOptions.scale;
        const y2 = rawY2;

        const rawDistance = getDistanceBetweenPoints(x1, y1, x2, y2);

        const distanceValue = parseFloat(rawDistance);
        const unit = displayUnit || "m";
        const distanceText = `${distanceValue.toFixed(2)} ${unit}`;

        const textSize = calculateTextDimensions(
          distanceText.toString(),
          13,
          "sans-serif"
        );

        // draw text to display distance
        measurementDataRef.current.textBackground = context.makeRectangle(
          endDotX,
          endDotY + Y_OFFSET,
          textSize.width + TEXT_PADDING,
          textSize.height
        );
        measurementDataRef.current.textBackground.fill = "white";
        measurementDataRef.current.distanceText = context.makeText(
          distanceText,
          endDotX,
          endDotY + Y_OFFSET
        );
      }
    }
    context.update();
    evt.preventDefault();
  };
  if (showMeasurement) {
    // eslint-disable-next-line listeners/matching-remove-event-listener -- The click listener reference is stored on measurementDataRef.current.clickEvent and removed in clearMeasurementData() when removeClickEvent=true. The indirection through the ref is intentional to support cleanup from external callers.
    context.renderer.domElement.addEventListener("click", eventListener);
    measurementDataRef.current.clickEvent = eventListener;
  }
};

// Remove all shapes from the Two.js instance and resets the refs
export function clearMeasurementData(
  measurementDataRef,
  context,
  removeClickEvent: boolean
) {
  if (measurementDataRef.current) {
    for (let i = 0; i < measurementDataRef.current.dots.length; i++) {
      const dot = measurementDataRef.current.dots[i];
      context.remove(dot);
    }
    context.remove(measurementDataRef.current.line);
    context.remove(measurementDataRef.current.distanceText);
    context.remove(measurementDataRef.current.textBackground);
    measurementDataRef.current.dotCounter = 0;
    measurementDataRef.current.dots.length = 0;
    measurementDataRef.current.line = null;
    measurementDataRef.current.distanceText = null;
    measurementDataRef.current.textBackground = null;
    if (removeClickEvent && measurementDataRef.current.clickEvent) {
      context.renderer.domElement.removeEventListener(
        "click",
        measurementDataRef.current.clickEvent
      );
      measurementDataRef.current.clickEvent = null;
    }
  }
}

// ─── Selected-well spacing measurement (EVA-29) ─────────────────────────────

export const SPACING_RING_RADIUS = 7;
const SPACING_BASELINE_OVERHANG = 40;
// Leader lines read as the connection between a well and the baseline, so they
// are drawn heavier than the chip borders.
const SPACING_LEADER_WIDTH = 2.5;
// Chip border. Neutral grey like the ipdb legend and the app's other
// tooltips/legends. It was neutral because the zone colour carried the meaning
// on the text; with the colouring parked (EVA-163 comment 163139) the whole
// chip is grey border over black text.
const SPACING_CHIP_BORDER = "#d9d9d9";
// Chip elevation. The app's popup/tooltip shadow is `--shadow-popup`, a
// layered box-shadow — which does nothing on an SVG shape, so its lower layers
// are reproduced with stacked CSS drop-shadow filters instead. Uses the same
// `--color-shadow-rgb` token so it tracks the theme.
// Deliberately darker than `--shadow-popup`'s own colour: that token is a pale
// grey-blue tuned for white chart backgrounds, and it disappears against the
// 3D Geo Model's saturated fills. Same layered shape, ink-coloured.
const SPACING_CHIP_SHADOW =
  "drop-shadow(0 1px 1.5px rgba(33, 33, 33, 0.30)) " +
  "drop-shadow(0 1px 3px rgba(33, 33, 33, 0.20))";
// The direction arrow is drawn as its own glyph node so it can be heavier and
// larger than the value it labels — as a character inside the value string it
// inherited the text weight and read too thin.
const SPACING_ARROW_SCALE = 1.3;
const SPACING_ARROW_WEIGHT = 700;
// Gap between the arrow column and the value column.
const SPACING_ARROW_GAP_EM = 0.22;
// Chips are kept this far inside the scene viewport. Anything drawn past the
// edge is clipped by the SVG, so an edge well's label would render half-cut.
const SPACING_VIEWPORT_MARGIN = 4;

// One ink for the whole spacing overlay — chips, arrows, leaders, ring and
// baseline. #212121 is what the ring and baseline were already drawn in.
// (EVA-163 comment 163139 — zone colouring off.)
const SPACING_LABEL_COLOR = "#212121";

// ── Relative label scaling (EVA-29, Chart Label Scaling design) ─────────────
// fontSize = clamp(10px, 0.011 × plotWidth, 22px); everything else is defined
// in em so a chip scales as one unit. 10px is EVA's legibility floor (its chart
// default is 12px), so below the floor the design says stop scaling and start
// dropping information instead — see SPACING_INK_BUDGET below.
export const SPACING_FONT_MIN = 10;
export const SPACING_FONT_MAX = 22;
const SPACING_FONT_PER_PLOT_WIDTH = 0.011;
// em-relative chip metrics.
const SPACING_CHIP_PAD_X_EM = 0.45; // each side
const SPACING_CHIP_HEIGHT_EM = 1.6;
// Each additional stacked row inside a chip. A grouped chip holds the
// horizontal distance above the vertical offset, so it is two rows tall.
const SPACING_CHIP_LINE_EM = 1.15;
const SPACING_CHIP_RADIUS_EM = 0.2;
const SPACING_CHIP_MIN_GAP_EM = 0.5; // between adjacent chips
const SPACING_LEADER_TO_CHIP_EM = 0.4; // dot/baseline to nearest chip edge
// Total label ink ceiling as a fraction of plot area. Past it, chips shed
// information rather than shrinking further: first the exact digits (compact
// "1.6k" form), then one label per well instead of two.
const SPACING_INK_BUDGET = 0.15;

// Registry of every Two.js object the spacing overlay added to a context.
// One registry per rendering context (live and screenshot canvases each get
// their own) so clears are complete — a leaked scene node was the EVA-6911
// failure mode.
export interface SpacingOverlayRegistry {
  objects: object[];
}

/**
 * Make every overlay node pointer-transparent.
 *
 * The overlay paints above the well shapes, so without this it swallows their
 * pointer events: moving onto a chip fires the well's `mouseleave`, which
 * clears the overlay, which puts the pointer back over the well — a
 * mouseenter/mouseleave flicker loop — and a click landing on a chip never
 * reaches the shape, so the bubble under it cannot be picked. The guide-lines
 * display this replaced disabled pointer events on every node it drew, for
 * exactly this reason.
 *
 * Must run AFTER a render pass: Two.js creates `_renderer.elem` during
 * `update()`, so styling before that silently does nothing.
 */
function disableOverlayPointerEvents(objects: object[]): void {
  for (const object of objects) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const elem = (object as any)?._renderer?.elem;
    if (elem?.style) {
      elem.style.pointerEvents = "none";
    }
  }
}

/**
 * Give the chip boxes the app's popup elevation.
 *
 * Applied to the box only (not its text, which would smear) and, like
 * `disableOverlayPointerEvents`, only AFTER a render pass — Two.js creates
 * `_renderer.elem` during `update()`.
 */
function applyChipShadows(chipBackgrounds: object[]): void {
  for (const chip of chipBackgrounds) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const elem = (chip as any)?._renderer?.elem;
    if (elem?.style) {
      elem.style.filter = SPACING_CHIP_SHADOW;
    }
  }
}

export function clearSpacingMeasurement(
  spacingDataRef: { current: SpacingOverlayRegistry },
  context
): void {
  if (!spacingDataRef?.current) {
    return;
  }
  for (const obj of spacingDataRef.current.objects) {
    context.remove(obj);
  }
  spacingDataRef.current.objects = [];
}

/**
 * Font size for the spacing labels at a given plotted width, per the Chart
 * Label Scaling design: `clamp(10px, 0.011 × plotWidth, 22px)`. Exported so
 * callers and tests can reason about the same number the overlay draws with.
 */
export const getSpacingFontSize = (plotWidth: number): number =>
  clampToRange(
    SPACING_FONT_PER_PLOT_WIDTH * plotWidth,
    SPACING_FONT_MIN,
    SPACING_FONT_MAX
  );

// jsdom's span-measurement returns 0×0, so chips fall back to an approximation
// that keeps placement math (and its tests) meaningful. Height is a pure em
// multiple so a chip's box scales with its text as one unit.
export interface SpacingChipRow {
  /** Direction glyph, drawn heavier than the value; "" at a rounded zero. */
  readonly arrow: string;
  readonly value: string;
}

// jsdom's span-measurement returns 0x0, so text falls back to an approximation
// that keeps placement math (and its tests) meaningful.
const textWidthAt = (text: string, fontSize: number): number => {
  if (!text) {
    return 0;
  }
  const dims = calculateTextDimensions(text, fontSize, "sans-serif");
  return dims?.width || text.length * fontSize * 0.6;
};

// Arrows share one column so the values line up beneath each other, which is
// the point of left-aligning the rows in the first place.
const measureChipAt = (
  rows: readonly SpacingChipRow[],
  fontSize: number
): { w: number; h: number; arrowColumn: number } => {
  const arrowFontSize = fontSize * SPACING_ARROW_SCALE;
  let arrowColumn = 0;
  let widestValue = 0;
  for (const row of rows) {
    arrowColumn = Math.max(arrowColumn, textWidthAt(row.arrow, arrowFontSize));
    widestValue = Math.max(widestValue, textWidthAt(row.value, fontSize));
  }
  const gap = arrowColumn > 0 ? SPACING_ARROW_GAP_EM * fontSize : 0;
  return {
    w: arrowColumn + gap + widestValue + 2 * SPACING_CHIP_PAD_X_EM * fontSize,
    h:
      SPACING_CHIP_HEIGHT_EM * fontSize +
      Math.max(rows.length - 1, 0) * SPACING_CHIP_LINE_EM * fontSize,
    arrowColumn
  };
};

/**
 * Draw the spacing overlay for the selected cross-section entry: a ring on its
 * center dot, a dashed measurement baseline through it, and for every other
 * entry a dashed vertical leader plus two white-backed chips — horizontal
 * distance at the baseline foot and vertical offset (↑/↓) along the leader.
 *
 * Everything is drawn in one black ink (`SPACING_LABEL_COLOR`). Chips, arrows
 * and leaders were coloured by the neighbour's octant zone until EVA-163
 * comment 163139; the classification still runs and still decides WHICH
 * neighbours are drawn, it just no longer decides their colour.
 *
 * `data` must be the SCALED scene data (the same object the rest of the scene
 * was drawn from); the layout x-scale is divided back out of Δx before any
 * distance/classification math, mirroring the Measurement ruler.
 *
 * `visibleZones` is the Class Filter (EVA-163): a neighbor whose zone is not in
 * the set is dropped entirely — no leader, no chip — so filtering to one class
 * also thins the plot. Omit it (or pass every zone) for the unfiltered display.
 *
 * `maxRadii` drops neighbors too far from the reference well to be worth
 * labelling (EVA-163), radially and vertically. Omit it for no limit.
 *
 * A neighbor scaled or panned/zoomed entirely outside the current viewport
 * (read live off `context.scene`, see the EVA-264 comment below) is dropped
 * the same way — no chip pinned to a meaningless edge position, since it
 * never reflected the well's true distance or direction anyway. Panning it
 * back into view restores its chip on the next redraw.
 *
 * Returns whether anything was drawn. NOTHING is drawn — not even the
 * reference well's ring or the baseline — when no neighbour survives to be
 * labelled, whether because the cross-section holds a single well or because
 * the filters admit no one. Callers need the answer to decide whether the old
 * Well Data value labels should stay hidden.
 */
export function drawSpacingMeasurement(
  context,
  data: XdaData,
  options: XdaLayoutOptions,
  screenSize: ScreenSize,
  xdaSettings: XDASettingsT,
  selectedEntryIndex: number,
  spacingDataRef: { current: SpacingOverlayRegistry },
  displayUnit: string,
  thresholds: OctantThresholds,
  visibleZones?: ReadonlySet<OctantZone>,
  maxRadii?: SpacingMaxRadii
): boolean {
  clearSpacingMeasurement(spacingDataRef, context);
  const rect = data?.rect;
  const centroids = data?.centroids ?? [];
  const selected = centroids[selectedEntryIndex];
  if (!rect || !selected || !screenSize || options.scale <= 0) {
    return false;
  }

  const objects = spacingDataRef.current.objects;
  // EVA-264: the pan/zoom ZUI applies to `context.scene` directly (see addZUI,
  // XdaViewer.tsx) and never triggers a redraw on its own — the chips below
  // are placed in the same untransformed "surface" coordinates fitPointToView
  // uses, so clamping them against the raw canvas box only matched the visible
  // viewport at the ZUI's identity transform. Reading the live scale/translation
  // here (defaulting to identity when absent, e.g. the fake context in tests)
  // keeps the clamp honest about where the viewport actually is right now.
  const viewScale = context?.scene?.scale ?? 1;
  const viewTranslateX = context?.scene?.translation?.x ?? 0;
  const viewTranslateY = context?.scene?.translation?.y ?? 0;
  const viewLeft = -viewTranslateX / viewScale;
  const viewTop = -viewTranslateY / viewScale;
  const viewRight = (screenSize.width - viewTranslateX) / viewScale;
  const viewBottom = (screenSize.height - viewTranslateY) / viewScale;
  // Labels scale with the plotted area, not the raw canvas: a widget shows the
  // same design at any size, and full screen is simply a bigger plot.
  const { actualWidth: plotWidth, actualHeight: plotHeight } = computeActualDimensions(
    options,
    screenSize,
    xdaSettings
  );
  const fontSize = getSpacingFontSize(plotWidth);
  const em = (multiple: number) => multiple * fontSize;
  const chipGap = em(SPACING_CHIP_MIN_GAP_EM);
  const leaderToChip = em(SPACING_LEADER_TO_CHIP_EM);
  // Memoised for the duration of this draw: calculateTextDimensions forces a
  // synchronous layout per call (append span → read clientWidth → remove), and
  // every label is measured 2-3× (the ink estimate, the side-offset, then the
  // chip itself). This runs on every mouseenter, so the repeats are the part
  // worth removing — font size is fixed per draw, so text alone keys the cache.
  const chipSizeCache = new Map<string, { w: number; h: number; arrowColumn: number }>();
  const measureChip = (rows: readonly SpacingChipRow[]) => {
    const key = rows.map((r) => `${r.arrow}\u0000${r.value}`).join("|");
    const cached = chipSizeCache.get(key);
    if (cached) {
      return cached;
    }
    const measured = measureChipAt(rows, fontSize);
    chipSizeCache.set(key, measured);
    return measured;
  };
  const toView = (cx: number, cy: number) =>
    fitPointToView(cx, cy, rect.width, rect.height, options, screenSize, xdaSettings);
  const [selX, selY] = toView(selected.x, selected.y);
  const screenPoints = centroids.map((c) => toView(c.x, c.y));

  // Per-neighbor geometry, computed once. Un-scale Δx only — options.scale is
  // pre-multiplied into x data coords by scaleData; y is never scaled (same
  // rule as the Measurement ruler).
  const admissibleNeighbors = centroids
    .map((centroid, index) => {
      if (index === selectedEntryIndex) {
        return null;
      }
      const dx = (centroid.x - selected.x) / options.scale;
      const dy = centroid.y - selected.y;
      const [px, py] = screenPoints[index];
      // Neighbour-radius caps (EVA-163). Applied BEFORE classification: a
      // well beyond the caps is out of the display entirely, so there is no
      // point deciding which zone it would have been.
      if (maxRadii && !isWithinSpacingMaxRadii(dx, dy, maxRadii)) {
        return null;
      }
      const zone = classifyOctantZone(dx, dy, thresholds);
      // Class Filter (EVA-163). Dropped here, before ANY drawing pass, so a
      // hidden neighbor also stops consuming label room: it is absent from
      // the collision set and from the ink estimate, which is what makes
      // filtering to one class de-crowd the plot rather than just hide chips.
      if (visibleZones && !visibleZones.has(zone)) {
        return null;
      }
      return {
        dx,
        dy,
        px,
        py,
        // Black, not the zone colour (EVA-163 comment 163139 — Travis). The
        // same value the ring and baseline already use, so the whole overlay
        // reads as one ink. Classification is unchanged: `zone` is still
        // computed above and still decides whether a neighbour is drawn at
        // all, it just no longer decides what colour it is.
        color: SPACING_LABEL_COLOR,
        // PARKED: color: OCTANT_ZONE_COLORS[zone],
        onBaseline: Math.abs(py - selY) < 1,
        wellIsAboveBaseline: py < selY,
        // EVA-264: whether this neighbor's SCREEN position currently falls
        // inside the pan/zoomed viewport. Kept as a flag rather than dropping
        // the neighbor outright, so a neighbor scrolled off-canvas can still
        // count toward "is there anyone to compare against" below — only its
        // chip/leader is withheld, not the whole overlay.
        inView: px >= viewLeft && px <= viewRight && py >= viewTop && py <= viewBottom
      };
    })
    .filter(Boolean);

  // NOTHING TO COMPARE TO — draw nothing at all. The ring and the baseline
  // exist only to support the chips: the ring says which well the distances
  // are measured FROM, the baseline is the datum the vertical offsets are
  // measured against. With no neighbour left to label they mark nothing, and a
  // lone ring with a stub of dashed line reads as a broken overlay rather than
  // an empty one. Covers a single-well cross-section, and equally a Class
  // Filter or distance cap that happens to admit no one. (EVA-163)
  //
  // This checks the ADMISSIBLE set, not the visible one: a neighbor panned or
  // zoomed off-canvas is still "someone to compare against", so the pinned
  // well's ring stays put while only its chip is withheld below.
  if (!admissibleNeighbors.length) {
    return false;
  }

  // EVA-264: a neighbor scaled/panned/zoomed entirely out of the current
  // viewport gets no chip or leader — an edge-clamped chip never showed the
  // well's true position or distance, it just gave a stray-looking label —
  // but it stays in `admissibleNeighbors` above so the ring/baseline don't
  // vanish just because every neighbor is momentarily off-canvas.
  const neighbors = admissibleNeighbors.filter((n) => n.inView);

  // Dashed measurement baseline through the selected center dot, spanning the
  // wells it actually measures against, with an overhang on both sides.
  //
  // Derived from the SURVIVING neighbours, not from every centroid: the
  // baseline is the datum the vertical offsets are measured against, so it
  // should span the offsets that exist. Spanning all of them meant a 500 ft
  // Max Neighbour Radius still drew a line out to the far edge of a wide
  // section, past wells that are no longer labelled — which undercuts the
  // cap's entire purpose and contradicts the empty-overlay rule a few lines
  // up. (EVA-163)
  //
  // EVA-264: skipped entirely when every admissible neighbour is currently
  // off-canvas — with no visible neighbour to span, this degenerates to a
  // zero-length stub centered on the selected well, which is exactly the
  // "lone ring with a stub of dashed line" look the empty-overlay rule above
  // exists to avoid. The ring below still marks the pin either way.
  if (neighbors.length) {
    let minX = selX;
    let maxX = selX;
    for (const { px } of neighbors) {
      if (px < minX) minX = px;
      if (px > maxX) maxX = px;
    }
    const baseline = context.makeLine(
      minX - SPACING_BASELINE_OVERHANG,
      selY,
      maxX + SPACING_BASELINE_OVERHANG,
      selY
    );
    baseline.stroke = "#212121";
    baseline.linewidth = 1.5;
    baseline.dashes = [6, 4];
    objects.push(baseline);
  }

  // Selection ring around the center dot (dot radius is 3) — indicates the
  // reference well without highlighting its bubble.
  const ring = context.makeCircle(selX, selY, SPACING_RING_RADIUS);
  ring.fill = "rgba(0,0,0,0)";
  ring.stroke = "#212121";
  ring.linewidth = 2;
  objects.push(ring);

  // Greedy chip placement: try each candidate y (two-row dodge alternating
  // sides of the anchor), then step downward until free. Chips must never
  // overlap and stay legible over any background via the solid white fill.
  const placed: { x: number; y: number; w: number; h: number }[] = [];
  // Chip boxes, kept apart from `objects` so the shadow pass can target them
  // without also hitting the text, leaders, ring or baseline.
  const chipBackgrounds: object[] = [];
  const intersectsPlaced = (x: number, y: number, w: number, h: number) =>
    placed.some(
      (c) =>
        Math.abs(c.x - x) * 2 < c.w + w + chipGap &&
        Math.abs(c.y - y) * 2 < c.h + h + chipGap
    );
  // Chart-tooltip-style chip (EVA-29 comment 162493): rounded white
  // background with a neutral grey border, black text, and a directional arrow
  // instead of a colored dot. (The border and text were zone-coloured until
  // EVA-163 comment 163139.) Candidate spots are tried in order; the first
  // collision-free one wins, else it steps downward.
  const drawChip = (
    rows: readonly SpacingChipRow[],
    candidates: { x: number; y: number }[],
    color: string,
    // Direction the last-resort nudge walks in (+1 down, -1 up). Chips should
    // drift AWAY from the baseline rather than always downward, which used to
    // strand them far from the well they belong to.
    stepSign = 1
  ) => {
    const { w, h, arrowColumn } = measureChip(rows);
    // Every candidate is pulled inside the CURRENT viewport (viewLeft/Top/
    // Right/Bottom above) before it is considered, so a chip can never be
    // placed (or nudged) past the visible edge where the SVG would clip it —
    // whether or not the user has panned/zoomed since this scene was drawn.
    // The margin is a local-space quantity like w/h, so it scales with zoom
    // too (a constant SCREEN-space margin regardless of ZUI scale).
    const viewportMargin = SPACING_VIEWPORT_MARGIN / viewScale;
    const fit = (spot: { x: number; y: number }) => ({
      x: clampToRange(
        spot.x,
        viewLeft + w / 2 + viewportMargin,
        viewRight - w / 2 - viewportMargin
      ),
      y: clampToRange(
        spot.y,
        viewTop + h / 2 + viewportMargin,
        viewBottom - h / 2 - viewportMargin
      )
    });
    let { x, y } = fit(candidates[0]);
    for (const candidate of candidates) {
      const spot = fit(candidate);
      if (!intersectsPlaced(spot.x, spot.y, w, h)) {
        x = spot.x;
        y = spot.y;
        break;
      }
    }
    let guard = 0;
    while (intersectsPlaced(x, y, w, h) && guard < 6) {
      ({ x, y } = fit({ x, y: y + stepSign * (h + chipGap) }));
      guard++;
    }
    const background = context.makeRoundedRectangle(
      x,
      y,
      w,
      h,
      em(SPACING_CHIP_RADIUS_EM)
    );
    background.fill = "#ffffff";
    background.stroke = SPACING_CHIP_BORDER;
    background.linewidth = 1;
    objects.push(background);
    chipBackgrounds.push(background);
    // Rows are stacked top-to-bottom about the chip's center, so the
    // horizontal distance reads above the vertical offset, and LEFT-ALIGNED so
    // the arrows and digits line up between rows (per the 162689 mockup) —
    // centering a short second row against a long first one reads as ragged.
    const rowHeight = em(SPACING_CHIP_LINE_EM);
    const firstRowY = y - ((rows.length - 1) * rowHeight) / 2;
    const rowX = x - w / 2 + em(SPACING_CHIP_PAD_X_EM);
    const valueX = rowX + arrowColumn + (arrowColumn > 0 ? em(SPACING_ARROW_GAP_EM) : 0);
    rows.forEach((row, index) => {
      const rowY = firstRowY + index * rowHeight;
      if (row.arrow) {
        const arrow = context.makeText(row.arrow, rowX, rowY);
        arrow.size = fontSize * SPACING_ARROW_SCALE;
        arrow.weight = SPACING_ARROW_WEIGHT;
        arrow.fill = color;
        arrow.alignment = "left";
        objects.push(arrow);
      }
      const label = context.makeText(row.value, valueX, rowY);
      label.size = fontSize;
      label.fill = color;
      label.alignment = "left";
      objects.push(label);
    });
    placed.push({ x, y, w, h });
  };

  // Full precision, e.g. "1,630 ft".
  const formatValue = (value: number) =>
    `${Math.round(Math.abs(value)).toLocaleString("en-US")} ${displayUnit}`;
  // Compact form the design switches to when the plot runs out of label room,
  // e.g. "1,630 ft" -> "1.6k ft". PRECISION is the only thing shed: the unit
  // stays. A bare "38.7" beside a bare "39" gives no way to tell a horizontal
  // distance in feet from a vertical one, and the crowded plot is exactly
  // where that ambiguity costs the most - which is where the design's original
  // "drop the unit too" saving was paid for out of the wrong budget.
  const formatCompact = (value: number) => {
    const rounded = Math.round(Math.abs(value));
    if (rounded < 1000) {
      return `${rounded} ${displayUnit}`;
    }
    return `${(rounded / 1000).toFixed(1).replace(/\.0$/, "")}k ${displayUnit}`;
  };

  // Pass 1 — every dashed leader (center dot → baseline), in the same black
  // ink as everything else. Drawn before ANY chip: Two.js paints in insertion
  // order, so
  // interleaving leaders with chips let a later well's leader cut straight
  // through an earlier well's label.
  for (const { px, py, color, onBaseline } of neighbors) {
    if (onBaseline) {
      continue;
    }
    const leader = context.makeLine(px, py, px, selY);
    leader.stroke = color;
    leader.linewidth = SPACING_LEADER_WIDTH;
    leader.dashes = [3, 3];
    objects.push(leader);
  }

  // One chip per neighbor carrying BOTH measurements as stacked rows (EVA-29
  // comment 162689): the horizontal distance sits ABOVE the vertical offset,
  // where the two used to be separate pills either side of the baseline.
  // Arrows: ←/→ for which side of the reference well, ↑/↓ for
  // shallower/deeper; neither at a rounded zero.
  // Both components always: a crowded plot sheds PRECISION, never one of the
  // two measurements — a chip showing only a horizontal distance cannot be
  // told apart from one whose vertical offset happens to be missing.
  const rowsFor = (n: (typeof neighbors)[number], compact: boolean): SpacingChipRow[] => {
    const format = compact ? formatCompact : formatValue;
    const horizontal = {
      arrow: Math.round(Math.abs(n.dx)) === 0 ? "" : n.dx < 0 ? "←" : "→",
      value: format(n.dx)
    };
    return [
      horizontal,
      {
        arrow: Math.round(Math.abs(n.dy)) === 0 ? "" : n.wellIsAboveBaseline ? "↑" : "↓",
        value: format(n.dy)
      }
    ];
  };

  // Below the font floor the design stops scaling and starts shedding detail.
  // The only lever is precision: exact digits give way to the compact form
  // ("1,630 ft" → "1.6k ft"). Dropping the vertical row was tried and rejected,
  // and so was dropping the unit — both components and their units have to
  // stay readable at any density.
  const inkOf = (compact: boolean) =>
    neighbors.reduce((total, n) => {
      const { w, h } = measureChip(rowsFor(n, compact));
      return total + w * h;
    }, 0);
  const inkBudget = SPACING_INK_BUDGET * plotWidth * plotHeight;
  // The compact form only shortens values of 1000 or more ("1,630 ft" ->
  // "1.6k ft"); below that it is byte-identical to the full form, because the
  // unit is kept at every density and toLocaleString adds no separator under
  // 1000. So on a plot whose distances are all sub-1000 — most metric scenes,
  // and plenty of imperial ones — switching would re-measure every chip and
  // emit exactly the same labels.
  //
  // Checked FIRST so the ink estimate is not paid for either. There is no
  // further rung below it: dropping the vertical row and dropping the unit
  // were both tried and rejected, so a crowded sub-1000 plot has no automatic
  // escalation left — the Class Filter and the neighbour-radius caps are the
  // levers for that case, which is part of why EVA-163 added them.
  const compactWouldShorten = neighbors.some(
    (n) => Math.round(Math.abs(n.dx)) >= 1000 || Math.round(Math.abs(n.dy)) >= 1000
  );
  const useCompact = compactWouldShorten && inkOf(false) > inkBudget;

  // Pass 2 — chips, so every label sits above every line.
  for (const n of neighbors) {
    const { px, py, color, wellIsAboveBaseline } = n;
    const rows = rowsFor(n, useCompact);

    // Centered on the well's CENTER DOT, on the far side of it from the
    // leader: the leader runs from the dot to the baseline, so the chip goes
    // below a dot whose leader climbs and above one whose leader descends. It
    // therefore never overlaps the line it belongs to. Collisions stack
    // further out on that side; the leader's side is the last resort.
    const awayFromLeaderSign = wellIsAboveBaseline ? -1 : 1;
    const { h: chipBoxHeight } = measureChip(rows);
    const dotClearance = chipBoxHeight / 2 + leaderToChip;
    // A rung must clear the chip's OWN height, not a one-row chip's: stepping
    // by 1.6em under a 2.75em grouped chip left every rung still colliding, so
    // the ladder was dead weight and only the nudge loop dodged — landing
    // chips further from their well than intended.
    const rowStep = chipBoxHeight + chipGap;
    drawChip(
      rows,
      [
        ...[0, 1, 2, 3].map((row) => ({
          x: px,
          y: py + awayFromLeaderSign * (dotClearance + row * rowStep)
        })),
        { x: px, y: py - awayFromLeaderSign * dotClearance }
      ],
      color,
      awayFromLeaderSign
    );
  }

  context.update();
  // After update(), so the rendered elements exist to be styled.
  disableOverlayPointerEvents(objects);
  applyChipShadows(chipBackgrounds);
  return true;
}
